import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Modal, Grid, Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TableSortLabel, useTheme, Checkbox, FormControlLabel, Divider, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { Card, CardContent } from "@mui/material";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import { AuthContext } from "../../context/Auth/AuthContext";

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from 'tss-react/mui';
import { green, red } from '@mui/material/colors';
import useDashboard from "../../hooks/useDashboard";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import {
    Facebook,
    Instagram,
    WhatsApp,
    FileDownloadOutlined,
    PrintOutlined,
    SearchOutlined,
    Language,
    Science,
    CompareArrows as CompareArrowsIcon,
} from "@mui/icons-material";
import StatTile from "../../components/Dashboard/StatTile";
import PeriodSelect from "../../components/PeriodSelect";
import { rangoDePeriodo } from "../../helpers/rangoDePeriodo";
import CompararAsesoresModal from "../../components/CompararAsesoresModal";
import SegmentedBar from "../../components/Dashboard/SegmentedBar";
import RatingStars from "../../components/Dashboard/RatingStars";
import DailyTrend from "../../components/Dashboard/DailyTrend";
import MultiArea from "../../components/Dashboard/MultiArea";
import MiniArea from "../../components/Dashboard/MiniArea";
import InfoHint from "../../components/Dashboard/InfoHint";
import AdsPanel from "../../components/Dashboard/AdsPanel";
import ColorModeContext from "../../context/ColorMode/ColorModeContext";
import "./print.css";
import { i18n } from "../../translate/i18n";

import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import GroupIcon from '@mui/icons-material/Group';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import HistoryIcon from '@mui/icons-material/History';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ReplayIcon from '@mui/icons-material/Replay';
import TimelineIcon from '@mui/icons-material/Timeline';

const useStyles = makeStyles()((theme, _params, classes) => ({
    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        padding: theme.spacing(1.25, 2),
        "&:last-child": {
            paddingBottom: theme.spacing(1.25),
        },
    },
    greetingRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    greetingAvatar: {
        width: 36,
        height: 36,
        borderRadius: "50%",
        flexShrink: 0,
        background: "linear-gradient(135deg, #c05f5f, #7a2c2c)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 15,
    },
    greetingText: {
        fontSize: 16.5,
        fontWeight: 400,
        lineHeight: 1.25,
    },
    greetingDate: {
        fontSize: 12.5,
        color: theme.palette.text.secondary,
        marginTop: 1,
    },
    filterRow: { display: "flex", alignItems: "center", gap: 8 },
    botonReporte: {
        height: 34,
        minHeight: 34,
        borderRadius: 8,
        fontSize: 13,
        textTransform: "none",
        whiteSpace: "nowrap",
        flexShrink: 0,
        paddingLeft: theme.spacing(1.75),
        paddingRight: theme.spacing(1.75),
    },
    filterColumn: {

        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 4,
    },
    bloqueAsesores: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1, 1.25),
        borderRadius: 8,
        backgroundColor: theme.palette.mode === "light" ? "#f6f5f0" : "#212120",
    },
    tituloBloqueAsesores: {
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    callsBreakdown: {
        display: "flex",
        flexDirection: "column",
        borderTop: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
        paddingTop: theme.spacing(1),
    },
    callsRow: {
        display: "grid",
        gridTemplateColumns: "auto 52px 52px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0.45, 1),
    },
    callsFooter: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: theme.spacing(0.5),
    },
    oppSplit: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: theme.spacing(2.5),
    },
    oppCol: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },
    oppColTitle: {
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: theme.palette.text.secondary,
        paddingBottom: theme.spacing(0.5),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
    },
    oppColNote: {
        fontSize: 10,
        color: theme.palette.text.disabled,
        marginTop: 3,
    },
    oppRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        padding: theme.spacing(0.55, 0),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
    },
    oppName: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
    },
    lineRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.5fr) 56px 76px 100px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0.6, 1),
        borderRadius: 6,
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
    },
    lineName: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
    },
    lineNumber: {
        fontSize: 11,
        color: theme.palette.text.disabled,
        display: "block",
    },
    clickableRow: {
        cursor: "pointer",
        borderRadius: 6,
        "&:hover": { backgroundColor: theme.palette.mode === "light" ? "#ecebe4" : "#2a2a28" },
    },
    clickableTitle: {
        cursor: "pointer",
        "&:hover": { textDecoration: "underline" },
    },
    commentCell: {
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    reopenHead: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr) 76px 54px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0, 1, 0.75, 1),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
        marginBottom: theme.spacing(0.25),
    },
    reopenRow: {
        position: "relative",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr) 76px 54px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0.6, 1),
        borderRadius: 6,
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
        overflow: "hidden",
    },
    reopenBar: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        backgroundColor: theme.palette.mode === "light" ? "rgba(198,40,40,0.06)" : "rgba(198,40,40,0.12)",
        zIndex: -1,
        transition: "width .2s ease",
    },
    reopenFast: {
        color: "#c62828",
        fontWeight: 700,
    },
    reopenGauge: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        padding: "10px 14px",
        borderRadius: 8,
        marginBottom: 14,
        borderLeft: "4px solid",
    },
    reopenGauge_ok: {
        background: "rgba(128,128,128,0.08)",
        borderLeftColor: "#9aa0a6",
    },
    reopenGauge_warn: {
        background: "rgba(180,83,9,0.12)",
        borderLeftColor: "#b45309",
    },
    reopenGauge_bad: {
        background: "rgba(198,40,40,0.12)",
        borderLeftColor: "#c62828",
    },
    reopenGaugeNum: {
        fontSize: 22,
        fontWeight: 800,
        marginRight: 8,
    },
    reopenGaugeText: {
        fontSize: 13,
        opacity: 0.85,
    },
    reopenGaugeVerdict: {
        fontSize: 12.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },
    reopenGaugeTrend: {
        fontSize: 12.5,
        fontWeight: 600,
        marginTop: 3,
    },
    reopenTrendUp: {
        color: "#c62828",
    },
    reopenTrendDown: {
        color: "#2e7d32",
    },
    queueColLabelLeft: {
        fontSize: 10.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    ratingHead: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 118px minmax(70px, 1fr) 54px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0, 1, 0.75, 1),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
        marginBottom: theme.spacing(0.25),
    },
    ratingRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 118px minmax(70px, 1fr) 54px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0.6, 1),
        borderRadius: 6,
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
    },
    ratingStarsCell: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
    },
    ratingAvgNum: {
        fontStyle: "normal",
        fontSize: 13,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
    },
    ratingSpread: {
        display: "flex",
        height: 7,
        borderRadius: 4,
        overflow: "hidden",
        gap: 2,
        backgroundColor: theme.palette.mode === "light" ? "#eeede7" : "#252523",
        "& i": { display: "block", minWidth: 3 },
    },
    ownRow: {
        backgroundColor: theme.palette.mode === "light" ? "#f2f6fc" : "#1d2430",
    },
    adHead: {
        display: "grid",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0, 1, 0.75, 1),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
        marginBottom: theme.spacing(0.25),
    },
    adRow: {
        display: "grid",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0.6, 1),
        borderRadius: 6,
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
        "&:hover": { backgroundColor: theme.palette.mode === "light" ? "#f6f5f0" : "#212120" },
    },
    clickableNum: {
        cursor: "pointer",
        "&:hover": { textDecoration: "underline" },
    },
    adCell: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    adMeta: {
        fontSize: 10.5,
        color: theme.palette.text.disabled,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    adName: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        minWidth: 0,
        lineHeight: 1.35,
    },
    connHead: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 92px 92px 92px 92px 62px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0, 1, 0.75, 1),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
        marginBottom: theme.spacing(0.25),
    },
    connRow: {
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 92px 92px 92px 92px 62px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0.6, 1),
        borderRadius: 6,
    },
    connClickable: {
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.mode === "light" ? "#f6f5f0" : "#212120" },
    },
    connName: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    connIcon: { display: "flex", alignItems: "center", flexShrink: 0, lineHeight: 0 },
    kpiConn: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        minWidth: 0,
        "& svg": { fontSize: 17 },
    },
    connText: {
        fontSize: 13.5,
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    queueHead: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 92px 92px 92px 62px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0, 1, 0.75, 1),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
        marginBottom: theme.spacing(0.25),
    },
    queueColLabel: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 5,
        fontSize: 10.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
    },
    queueColTotalLabel: {
        textAlign: "right",
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: theme.palette.text.secondary,
        paddingRight: theme.spacing(0.5),
    },
    queueGroupRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(1.25, 1, 0.4, 1),
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: theme.palette.text.secondary,
    },
    queueGroupName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    queueGroupTotal: { fontVariantNumeric: "tabular-nums", paddingRight: theme.spacing(0.5) },
    queueRow: {
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#f0efe9" : "#232322"}`,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 92px 92px 92px 62px",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(0.6, 1),
        borderRadius: 6,
        "&:hover": { backgroundColor: theme.palette.mode === "light" ? "#f6f5f0" : "#212120" },
    },
    queueName: {
        fontSize: 13.5,
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
    },
    cellNum: {
        textAlign: "right",
        fontSize: 17,
        fontWeight: 600,
        lineHeight: 1.2,
        fontVariantNumeric: "tabular-nums",
    },
    cellTotal: {
        textAlign: "right",
        fontSize: 18,
        fontWeight: 700,
        lineHeight: 1.2,
        fontVariantNumeric: "tabular-nums",
        paddingRight: theme.spacing(0.5),
    },
    cellZero: { color: theme.palette.text.disabled },
    nombreAsesorAmbito: {
        flex: 1,
        minWidth: 0,
        fontSize: 12.5,
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    totalAmbito: {
        width: 44,
        textAlign: "right",
        fontWeight: 700,
        fontSize: 13.5,
        fontVariantNumeric: "tabular-nums",
        flexShrink: 0,
    },
    pctAmbito: {
        width: 44,
        textAlign: "right",
        fontSize: 11.5,
        color: theme.palette.text.secondary,
        fontVariantNumeric: "tabular-nums",
        flexShrink: 0,
    },
    filaAsesorAmbito: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 1, 0.5, 3.5),
    },
    ambitoActivo: {
        backgroundColor: theme.palette.mode === "light" ? "#f0efe9" : "#242422",
    },
    closureListBox: {
        maxHeight: 320,
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    closureRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        fontSize: 12,
        padding: theme.spacing(0.4, 0),
    },
    closureContact: {
        flex: "1 1 32%",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontWeight: 600,
    },
    closureReason: {
        flex: "1 1 30%",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: theme.palette.text.secondary,
    },
    closureChat: {
        flexShrink: 0,
        width: 62,
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
    },
    closureDate: {
        flexShrink: 0,
        color: theme.palette.text.disabled,
        fontVariantNumeric: "tabular-nums",
    },
    listaVacia: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        padding: theme.spacing(1, 0),
    },
    modalAsesor: {
        padding: theme.spacing(3),
        borderRadius: 10,
        width: 820,
        maxWidth: "94vw",
        margin: "7vh auto",
        outline: "none",
    },
    modalAsesorCabecera: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
        paddingBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e1e0d9" : "#2c2c2a"}`,
    },
    modalAsesorCifras: {
        display: "flex",
        gap: theme.spacing(3),
    },
    cifra: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
    },
    cifraNum: {
        fontSize: 22,
        fontWeight: 600,
        lineHeight: 1.1,
    },
    cifraLbl: {
        fontSize: 10.5,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: theme.palette.text.secondary,
    },
    modalAsesorCuerpo: {
        display: "flex",
        gap: theme.spacing(3),
        marginTop: theme.spacing(2),
        alignItems: "flex-start",
        flexWrap: "wrap",
    },
    columnaAmbitos: {
        flex: "0 0 240px",
        minWidth: 200,
    },
    columnaLista: {
        flex: "1 1 380px",
        minWidth: 0,
    },
    motivosCierreBox: {
        marginBottom: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    motivosCierreTitulo: {
        fontSize: "0.68rem",
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.75),
    },
    ambitoItem: {
        cursor: "pointer",
        padding: theme.spacing(0.75, 1),
        borderRadius: 6,
        marginBottom: theme.spacing(0.5),
        "&:hover": {
            backgroundColor: theme.palette.mode === "light" ? "#f0efe9" : "#242422",
        },
    },
    ambitoItemTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: theme.spacing(1),
        marginBottom: 4,
    },
    ambitoItemNombre: {
        fontSize: 13,
        fontWeight: 600,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    ambitoItemNum: {
        fontSize: 13,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
    },
    ambitoItemPct: {
        fontSize: 10.5,
        color: theme.palette.text.secondary,
    },
    closureHeadRow: {
        fontSize: 10.5,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e1e0d9" : "#2c2c2a"}`,
        paddingBottom: theme.spacing(0.5),
        fontWeight: 600,
    },
    closureLine: {
        flex: "0 0 25%",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: theme.palette.text.secondary,
    },
    modalAsesorNombre: {
        fontSize: 19,
        fontWeight: 700,
        lineHeight: 1.2,
    },
    modalAsesorSub: {
        fontSize: 11.5,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    previaMarco: {
        position: "relative",
        width: `min(100%, calc((94vh - 230px) * ${A4_RATIO}))`,
        aspectRatio: `${A4_RATIO}`,
        margin: "0 auto",
        border: "1px solid rgba(0,0,0,0.22)",
        borderRadius: 3,
        background: "#ffffff",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.28)",
    },
    previaVisor: {
        width: "100%",
        height: "100%",
        border: "none",
        display: "block",
    },
    previaCapa: {
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
        background: "rgba(255,255,255,0.92)",
    },
    previaAviso: {
        fontSize: 12.5,
        color: "#6b7280",
    },
    previaPieFila: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(0.5),
    },
    previaPie: {
        fontSize: 11,
        color: theme.palette.text.secondary,
        textAlign: "center",
        marginTop: 8,
    },
    modalConfigPdf: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 1240,
        maxWidth: "96vw",
        maxHeight: "94vh",
        display: "flex",
        flexDirection: "column",
        outline: "none",
        overflow: "hidden",
    },
    configCabecera: {
        padding: theme.spacing(2.5, 3, 2),
    },
    configCuerpo: {
        display: "flex",
        gap: theme.spacing(3),
        padding: theme.spacing(2, 3, 2.5),
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
    },
    configLista: {
        flex: "0 0 270px",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
    },
    configListaScroll: {
        overflowY: "auto",
        minHeight: 0,
        paddingRight: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    configBuscar: {
        flexShrink: 0,
    },
    configVacio: {
        fontSize: 12.5,
        color: theme.palette.text.secondary,
        fontStyle: "italic",
        padding: theme.spacing(1, 0),
    },
    configPrevia: {
        flex: 1,
        minWidth: 0,
        overflowY: "auto",
        minHeight: 0,
    },
    configGrupo: {
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.7,
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    configGrupoBloque: {
        marginBottom: theme.spacing(2),
    },
    configOpcion: {
        marginLeft: -9,
        marginRight: 0,
        width: "100%",
        alignItems: "flex-start",
        marginBottom: theme.spacing(0.75),
    },
    opcionTitulo: {
        fontSize: 13,
        lineHeight: 1.25,
    },
    opcionDesc: {
        fontSize: 11,
        lineHeight: 1.3,
        color: theme.palette.text.secondary,
    },
    configPie: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1.75, 3),
    },
    modalDetalle: {
        padding: 20,
        borderRadius: 8,
        width: 800,
        maxWidth: "800px",
        margin: "100px auto",
        backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#212120",
        color: theme.palette.text.primary,
        outline: "none",
        border: `1px solid ${theme.palette.mode === "light" ? "#e3e2dc" : "#33322f"}`
    },
    tituloBloqueModal: {

        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.25),
    },
    tituloBloqueModalConTotal: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
    tituloBloqueModalTotal: {
        fontSize: 12,
        fontWeight: 700,
        color: theme.palette.text.primary,
        whiteSpace: "nowrap",
    },
    modalAsesorPie: {
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2.5),
    },
    filaAsesor: {
        cursor: "pointer",
    },
    filaPropia: {
        backgroundColor: theme.palette.mode === "light" ? "#f4ebeb" : "#2a2020",
        "& td:first-of-type": {
            fontWeight: 700,
            boxShadow: `inset 3px 0 0 ${theme.palette.mode === "light" ? "#a34a4a" : "#c05f5f"}`,
        },
    },
    sectionHeader: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(0.75),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
        marginBottom: theme.spacing(1),
        [`& .${classes.sectionTitle}`]: {
            borderBottom: "none",
            paddingBottom: 0,
            marginBottom: 0,
        },
    },
    sectionScope: {
        fontSize: 11.5,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flexShrink: 1,
        minWidth: 0,
    },
    dateFilterControl: {
        display: "flex",
        alignItems: "stretch",
        height: 34,
        border: `1px solid ${theme.palette.mode === "light" ? "#c3c2b7" : "#383835"}`,
        borderRadius: 8,
        overflow: "hidden",
        flexShrink: 0,
    },
    dateFilterInput: {
        border: "none",
        outline: "none",
        background: "transparent",
        color: theme.palette.text.primary,
        fontSize: 13,
        fontFamily: theme.typography.fontFamily,
        padding: theme.spacing(0, 1.25),
        borderRight: `1px solid ${theme.palette.mode === "light" ? "#c3c2b7" : "#383835"}`,
        colorScheme: theme.palette.mode === "light" ? "light" : "dark",
    },
    groupFilterSelect: {
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
        border: "none",
        outline: "none",
        background: "transparent",
        backgroundImage:
            "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23888880' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 9px center",
        color: theme.palette.text.primary,
        fontSize: 13,
        fontFamily: theme.typography.fontFamily,
        padding: theme.spacing(0, 3.25, 0, 1.25),
        cursor: "pointer",
        maxWidth: 165,
        textOverflow: "ellipsis",
        borderRight: `1px solid ${theme.palette.mode === "light" ? "#c3c2b7" : "#383835"}`,
        transition: "background-color 0.15s ease",
        "&:hover": {
            backgroundColor: theme.palette.mode === "light" ? "#f0efe9" : "#242422",
        },
    },
    dateFilterButton: {
        borderRadius: 0,
        boxShadow: "none",
        height: "100%",
        fontSize: 12.5,
        fontWeight: 600,
        padding: theme.spacing(0, 2),
        minWidth: 0,
        whiteSpace: "nowrap",
    },
    sectionPaper: {
        padding: theme.spacing(2, 2.5),
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        fontSize: 14.5,
        fontWeight: 700,
        lineHeight: 1.3,
        paddingBottom: theme.spacing(0.75),
        borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29"}`,
        marginBottom: theme.spacing(1),
        whiteSpace: "nowrap",
        overflow: "hidden",
        "& svg": {
            fontSize: 17,
            flexShrink: 0,
            color: theme.palette.text.secondary,
        },
    },
    barRowRail: {
        flex: 1,
        height: 16,
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: theme.palette.mode === "light" ? "#e1e0d9" : "#2c2c2a",
    },
    barRowFill: {
        display: "flex",
        height: "100%",
        borderRadius: 8,
        overflow: "hidden",
        transition: "width 0.3s ease",
    },
    emptyState: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        textAlign: "center",
        padding: theme.spacing(2, 0),
    },
    scrollBox: {
        maxHeight: 320,
        overflowY: "auto",
        overflowX: "hidden",
        ...theme.scrollbarStyles,
    },
}));

const STATUS_TOKENS = {
    good: "#0ca30c",
    warning: "#fab219",
};

const RATING_COLORS = ["#c62828", "#ef6c00", "#eda100", "#7cb342", "#0a7a33"];

const COLORES_AMBITO = ["#2a78d6", "#008300", "#eda100", "#4a3aa7"];

const STATUS_COLORS = {
    open: { light: "#2a78d6", dark: "#3987e5" },
    pending: { light: "#eda100", dark: "#c98500" },
    closed: { light: "#008300", dark: "#008300" },
    bot: { light: "#4a3aa7", dark: "#9085e9" },
    neutral: { light: "#9aa0a6", dark: "#6b7076" },
};

const REOPEN_TARGET_DEFAULT = 10;

const REOPEN_BAD_FACTOR = 2;

const SIN_AMBITO = "Sin clasificar";

const rangoComparacion = (periodo, diaSuelto) => {
    switch (periodo) {
        case "semana":
            return {
                from: moment().subtract(1, "week").startOf("isoWeek"),
                to: moment().subtract(1, "week").endOf("isoWeek")
            };
        case "semanaPasada":
            return {
                from: moment().subtract(2, "week").startOf("isoWeek"),
                to: moment().subtract(2, "week").endOf("isoWeek")
            };
        case "mes":
            return {
                from: moment().subtract(1, "month").startOf("month"),
                to: moment().subtract(1, "month").endOf("month")
            };
        case "mesPasado":
            return {
                from: moment().subtract(2, "month").startOf("month"),
                to: moment().subtract(2, "month").endOf("month")
            };
        case "tresMeses":
            return { from: moment().subtract(6, "month"), to: moment().subtract(3, "month") };
        case "ayer":
            return { from: moment().subtract(8, "day"), to: moment().subtract(8, "day") };

        case "hoyAyer":
            return {
                from: moment().subtract(1, "day"),
                to: moment().subtract(1, "day")
            };
        case "dia":
            return {
                from: moment(diaSuelto).subtract(1, "week"),
                to: moment(diaSuelto).subtract(1, "week")
            };
        case "hoy":
        default:
            return { from: moment().subtract(1, "week"), to: moment().subtract(1, "week") };
    }
};

const soloObservacion = (cuerpo) => {
    if (!cuerpo) return "";
    const marca = "*Observación*:";
    const i = cuerpo.indexOf(marca);
    return (i === -1 ? cuerpo : cuerpo.slice(i + marca.length)).trim();
};

const PERIODOS = ["hoy", "ayer", "hoyAyer", "semana", "semanaPasada", "mes", "mesPasado", "tresMeses", "dia"];

const BLOQUES_PDF = [
    { clave: "cifras", etiqueta: "Resumen del periodo", grupo: "Cifras",
      desc: "Las ocho cifras de cabecera, comparadas con el periodo anterior." },
    { clave: "senales", etiqueta: "Señales automáticas", grupo: "Cifras",
      desc: "Lo que salta solo: carga concentrada, gente parada, tiempos fuera de objetivo." },

    { clave: "equipoTabla", etiqueta: "Chats por asesor", grupo: "Equipo",
      desc: "La tabla entera: quién trabaja, atiende, cierra y en qué acaba." },
    { clave: "pendientes", etiqueta: "Pendientes en sus colas · ahora mismo", grupo: "Equipo",
      desc: "Por asesor: chats sin tomar en las colas donde está cada uno. No cambia con las fechas." },
    { clave: "calificaciones", etiqueta: "Calificación por asesor", grupo: "Equipo",
      desc: "Nota media que le pone el cliente a cada uno." },

    { clave: "diaADia", etiqueta: "Día a día", grupo: "Operación",
      desc: "Chats que entran contra chats que se cierran, día por día." },
    { clave: "tiempos", etiqueta: "Tiempos de espera y atención · equipo", grupo: "Operación",
      desc: "Espera y atención de todo el equipo, en mediana y P90." },
    { clave: "colas", etiqueta: "Carga por departamento · ahora mismo", grupo: "Operación",
      desc: "Lo que está sin resolver ahora mismo. No cambia aunque cambies las fechas." },
    { clave: "conexiones", etiqueta: "Chats por conexión · con actividad", grupo: "Operación",
      desc: "Chats que tuvieron movimiento en las fechas elegidas, por la línea por donde entraron." },

    { clave: "nuevos", etiqueta: "Nuevos frente a recurrentes", grupo: "Resultado",
      desc: "Cuántos escriben por primera vez y cuántos ya son clientes." },
    { clave: "ambito", etiqueta: "Ámbito de cierre", grupo: "Resultado",
      desc: "En qué acaban los chats, según las categorías de cierre de la empresa." },
    { clave: "reaperturas", etiqueta: "Reaperturas por ámbito", grupo: "Resultado",
      desc: "En qué ámbito estaban los cierres que no resolvieron: el cliente volvió a escribir." },
    { clave: "oportunidades", etiqueta: "Oportunidades del periodo", grupo: "Resultado",
      desc: "Cómo se movieron entre estados en estas fechas." },
    { clave: "cartera", etiqueta: "Cartera de oportunidades", grupo: "Resultado",
      desc: "En qué estado está hoy toda la cartera viva." },
    { clave: "anunciosMeta", etiqueta: "Anuncios · Meta", grupo: "Resultado",
      desc: "Invertido, conversaciones, costo, cierres por ámbito y campañas de Meta. Cuenta todo cierre, aunque lo haga un admin." },
    { clave: "anunciosGoogle", etiqueta: "Anuncios · Google Ads", grupo: "Resultado",
      desc: "Invertido, clics, tickets, costo, cierres por ámbito y campañas de Google Ads. Cuenta todo cierre, aunque lo haga un admin." },
];

const A4_RATIO = 841.89 / 595.28;

const RETARDO_PREVIA = 600;

const BLOQUES_PDF_DEFECTO = ["cifras", "senales", "diaADia", "equipoTabla"];

const CLAVE_BLOQUES_PDF = "dashboard:bloquesPdf";

const leerBloquesGuardados = () => {
    try {
        const crudo = window.localStorage.getItem(CLAVE_BLOQUES_PDF);
        if (!crudo) return BLOQUES_PDF_DEFECTO;

        const guardado = JSON.parse(crudo);
        if (!Array.isArray(guardado)) return BLOQUES_PDF_DEFECTO;

        const validas = [...new Set(
            guardado.flatMap((c) =>
                c === "anuncios"
                    ? ["anunciosMeta", "anunciosGoogle"]
                    : c === "equipoCierres" || c === "equipoTiempos"
                    ? ["equipoTabla"]
                    : c === "motivos"
                    ? ["ambito"]
                    : [c]
            )
        )].filter((c) => BLOQUES_PDF.some((b) => b.clave === c));
        return validas.length ? validas : BLOQUES_PDF_DEFECTO;
    } catch {
        return BLOQUES_PDF_DEFECTO;
    }
};

const DashboardGeneral = () => {
    const { classes, cx } = useStyles();
    const { find } = useDashboard();
    const ultimosParams = useRef(null);
    const [loading, setLoading] = useState(false);
    const [ticketsByQueue, setTicketsByQueue] = useState([]);
    const [ticketsByUser, setTicketsByUser] = useState([]);
    const [ticketsByStatus, setTicketsByStatus] = useState([]);
    const [ticketByConection, setTicketByConection] = useState([]);
    const [ticketsByAd, setTicketsByAd] = useState([]);
    const [callsSummary, setCallsSummary] = useState({ total: 0, rated: 0, avgRating: null });
    const [callsByLine, setCallsByLine] = useState([]);
    const [workingAgents, setWorkingAgents] = useState(0);
    const [dailyChats, setDailyChats] = useState({ contacts: 0, chats: 0 });
    const [flowSummary, setFlowSummary] = useState({
        waitP50: null, waitP90: null, handleP50: null, handleP90: null,
        transfers: 0, returns: 0, reopens: 0
    });
    const [agenteSeleccionado, setAgenteSeleccionado] = useState("");
    const [agentesDelGrupo, setAgentesDelGrupo] = useState([]);
    const [ticketDetailByConection, setTicketDetailByConection] = useState([]);
    const [ticketDetailByStatus, setTicketDetailByStatus] = useState([]);

    const [ticketsTotal, setTicketsTotal] = useState([]);
    const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
    const [periodo, setPeriodo] = useState("hoy");

    const [queueGroups, setQueueGroups] = useState([]);
    const [grupoSeleccionado, setGrupoSeleccionado] = useState("");
    const [openModalByConection, setOpenModalByConection] = useState(false);
    const [openModalByStatus, setOpenModalByStatus] = useState(false);

    const [asesorDetalle, setAsesorDetalle] = useState(null);
    const [slaTargets, setSlaTargets] = useState(null);
    const [firstReplyP50, setFirstReplyP50] = useState(null);
    const [adDetail, setAdDetail] = useState(null);
    const [adRows, setAdRows] = useState([]);
    const [adLoading, setAdLoading] = useState(false);
    const [adsDaily, setAdsDaily] = useState([]);
    const [scopesDaily, setScopesDaily] = useState([]);
    const [dailySeries, setDailySeries] = useState([]);

    const [cierresPorDia, setCierresPorDia] = useState([]);
    const [newVsReturning, setNewVsReturning] = useState({ news: 0, returning: 0 });
    const [previous, setPrevious] = useState(null);
    const [reopensByReason, setReopensByReason] = useState([]);
    const [reopensDaily, setReopensDaily] = useState([]);
    const maxReopens = useMemo(
        () => Math.max(1, ...reopensByReason.map((r) => r.total)),
        [reopensByReason]
    );
    const [ratingsByUser, setRatingsByUser] = useState([]);
    const [ratingScale, setRatingScale] = useState(3);
    const [opportunityFlow, setOpportunityFlow] = useState([]);
    const [opportunityStock, setOpportunityStock] = useState([]);
    const [opportunityFlowDaily, setOpportunityFlowDaily] = useState([]);
    const [modalNotas, setModalNotas] = useState(null);
    const [listaNotas, setListaNotas] = useState([]);
    const [cargandoNotas, setCargandoNotas] = useState(false);
    const [modalLlamadas, setModalLlamadas] = useState(false);
    const [listaLlamadas, setListaLlamadas] = useState([]);
    const [cargandoLlamadas, setCargandoLlamadas] = useState(false);
    const [ambitoAbierto, setAmbitoAbierto] = useState(null);
    const [ambitosDesplegados, setAmbitosDesplegados] = useState([]);
    const [agentClosures, setAgentClosures] = useState([]);
    const [closureList, setClosureList] = useState(null);
    const [closureRows, setClosureRows] = useState([]);
    const [closureLoading, setClosureLoading] = useState(false);
    const [cargandoLista, setCargandoLista] = useState(false);

    const [selectedUser, setSelectedUser] = useState(null);

    const { user } = useContext(AuthContext);
    const theme = useTheme();
    const mode = theme.palette.mode === "dark" ? "dark" : "light";
    const colorOf = (key) => STATUS_COLORS[key][mode];

    const motivosCierreAsesor = useMemo(() => {
        const conteos = {};
        agentClosures.forEach((f) => {
            const clave = (f.reason || '').trim() || i18n.t('dashboardGeneral.sections.noReason');
            conteos[clave] = (conteos[clave] || 0) + 1;
        });
        const paleta = [colorOf('open'), colorOf('pending'), colorOf('bot'), '#8e44ad', '#16a085', '#d35400', '#7f8c8d'];
        return Object.entries(conteos)
            .sort((a, b) => b[1] - a[1])
            .map(([name, value], i) => ({ name, value, color: paleta[i % paleta.length] }));
    }, [agentClosures]);

    const objetivo = (valorMin, objetivoMin) => {
        if (!slaTargets || valorMin == null || !objetivoMin) return {};
        return {
            target: objetivoMin + " min",
            targetMet: valorMin <= objetivoMin,
            targetLabel: slaTargets.configured
                ? i18n.t('dashboardGeneral.flow.target') + ": " + objetivoMin + " min"
                : i18n.t('dashboardGeneral.flow.defaultTarget') + ": " + objetivoMin + " min",
        };
    };

    const variacion = (actual, anterior) => {
        if (anterior == null || anterior === 0) return null;
        return Math.round(((actual - anterior) / anterior) * 100);
    };

    const totalQuienesEscribieron = newVsReturning.news + newVsReturning.returning;
    const porcentajeDe = (n) => totalQuienesEscribieron > 0
        ? Math.round((n / totalQuienesEscribieron) * 100) + '%'
        : undefined;

    const rotuloComparacion = ["hoy", "ayer", "hoyAyer", "dia"].includes(periodo)
        ? i18n.t('dashboardGeneral.vsPrev')
        : i18n.t('dashboardGeneral.vsPeriod');

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('open');

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const TOPE_AMBITOS = 5;

    const { ambitos, ambitosOcultos } = useMemo(() => {
        const totales = new Map();
        ticketsByUser.forEach((u) => (u.categories || []).forEach((c) => {
            if (c.name) totales.set(c.name, (totales.get(c.name) || 0) + (c.total || 0));
        }));

        const ordenados = [...totales.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([nombre]) => nombre);

        if (ordenados.length <= TOPE_AMBITOS) {
            return { ambitos: ordenados.sort((a, b) => a.localeCompare(b)), ambitosOcultos: [] };
        }

        const visibles = ordenados.slice(0, TOPE_AMBITOS - 1);
        return {
            ambitos: visibles.sort((a, b) => a.localeCompare(b)),
            ambitosOcultos: ordenados.slice(TOPE_AMBITOS - 1)
        };
    }, [ticketsByUser]);

    const todosLosAsesores = useMemo(
        () => ticketsByUser
            .map((u) => {
                const porAmbito = {};
                ambitos.forEach((a) => { porAmbito["amb_" + a] = 0; });
                porAmbito["amb_otros"] = 0;
                (u.categories || []).forEach((c) => {
                    if (!c.name) return;
                    if (ambitosOcultos.includes(c.name)) {
                        porAmbito["amb_otros"] += c.total;
                    } else {
                        porAmbito["amb_" + c.name] = c.total;
                    }
                });
                return {
                    ...u,
                    ...porAmbito,
                    activo: (u.pending || 0) + (u.open || 0)
                };
            }),
        [ticketsByUser, ambitos, ambitosOcultos]
    );

    const usersWithActivo = useMemo(
        () => todosLosAsesores.filter(
            (u) =>
                (u.open || 0) > 0 ||
                (u.closed || 0) > 0 ||
                (u.bot || 0) > 0 ||
                (u.attended || 0) > 0
        ),
        [todosLosAsesores]
    );

    const [compararAbierto, setCompararAbierto] = useState(false);

    const sortedData = [...usersWithActivo].sort((a, b) => {
        const x = a[orderBy];
        const y = b[orderBy];

        const xVacio = x === null || x === undefined || x === '';
        const yVacio = y === null || y === undefined || y === '';
        if (xVacio && yVacio) return 0;
        if (xVacio) return 1;
        if (yVacio) return -1;

        if (x === y) return 0;
        if (order === 'asc') {
            return x < y ? -1 : 1;
        }
        return x > y ? -1 : 1;
    });

    useEffect(() => {
        async function cargaInicial() {
            let primero = "";
            try {
                const { data } = await api.get("/queue-group", { params: { mine: true } });
                const grupos = Array.isArray(data) ? data : [];
                setQueueGroups(grupos);
            } catch (err) {
                console.error("No se pudieron cargar los grupos de colas:", err?.message);
            }
            await fetchData({ grupo: primero, agente: "" });
        }
        setTimeout(() => {
            cargaInicial();
        }, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCambioGrupo = async (valor) => {
        setGrupoSeleccionado(valor);
        setAgenteSeleccionado("");
        await fetchData({ grupo: valor, agente: "" });
    };

    const handleCambioPeriodo = async (valor) => {
        setPeriodo(valor);
        await fetchData({ periodo: valor });
    };

    const handleCambioAgente = async (valor) => {
        setAgenteSeleccionado(valor);
        await fetchData({ agente: valor });
    };

    const groupedTickets = useMemo(() => {
        const grouped = {};
        ticketByConection.forEach(ticket => {
            if (!grouped[ticket.name]) {
                grouped[ticket.name] = {
                    name: ticket.name, channel: ticket.channel, total: 0, details: [],
                    open: 0, pending: 0, bot: 0, closed: 0
                };
            }
            const g = grouped[ticket.name];
            g.total += 1;
            if (ticket.status in { open: 1, pending: 1, bot: 1, closed: 1 }) g[ticket.status] += 1;
            g.details.push(ticket);
        });
        return Object.values(grouped).sort((a, b) => b.total - a.total);
    }, [ticketByConection]);

    const groupedTicketsByStatus = useMemo(() => {
        const grouped = {};
        ticketsByStatus.forEach(ticket => {
            if (!grouped[ticket.status]) {
                grouped[ticket.status] = { name: ticket.status, channel: ticket.channel, total: 0, details: [] };
            }
            grouped[ticket.status].total += 1;
            grouped[ticket.status].details.push(ticket);
        });
        return Object.values(grouped);
    }, [ticketsByStatus]);

    const userStatusTotals = useMemo(() => {
        const totals = {};
        (ticketDetailByStatus?.details || []).forEach(ticket => {
            const user = ticket?.user || i18n.t('dashboardGeneral.noUser');
            totals[user] = (totals[user] || 0) + 1;
        });
        return totals;
    }, [ticketDetailByStatus]);

    const filteredStatusTickets = useMemo(() => {
        if (!selectedUser) return ticketDetailByStatus?.details || [];
        return (ticketDetailByStatus?.details || []).filter(ticket => (ticket?.user || i18n.t('dashboardGeneral.noUser')) === selectedUser);
    }, [ticketDetailByStatus, selectedUser]);

    const colorMode = useContext(ColorModeContext);
    const [imprimiendo, setImprimiendo] = useState(false);
    const [logoImpresion, setLogoImpresion] = useState("");
    const [colorImpresion, setColorImpresion] = useState("#a13a3a");

    const imprimir = async () => {
        if (imprimiendo) return;
        setImprimiendo(true);

        try {
            const { data } = await api.get("/settings");
            const lista = Array.isArray(data) ? data : [];
            const logo = lista.find((d) => d.key === "logo")?.value;
            const color = lista.find((d) => d.key === "brandColor")?.value;
            setLogoImpresion(logo ? `${process.env.REACT_APP_BACKEND_URL}/public/logos/${logo}` : "");
            if (/^#[0-9a-f]{6}$/i.test(String(color || ""))) setColorImpresion(color);
        } catch {
            setLogoImpresion("");
        }

        const volverAOscuro = colorMode?.mode === "dark";
        if (volverAOscuro) colorMode.toggleColorMode();

        document.documentElement.classList.add("modo-impresion");
        window.dispatchEvent(new Event("resize"));

        const terminar = () => {
            window.removeEventListener("afterprint", terminar);
            document.documentElement.classList.remove("modo-impresion");
            window.dispatchEvent(new Event("resize"));
            if (volverAOscuro) colorMode.toggleColorMode();
            setImprimiendo(false);
        };

        window.addEventListener("afterprint", terminar);

        setTimeout(() => {
            window.print();
        }, 900);
    };

    const periodoImpresion = useMemo(() => {
        const r = rangoDePeriodo(periodo, date);
        const desde = r.from.clone().format("DD/MM/YYYY");
        const hasta = r.to.clone().format("DD/MM/YYYY");
        const etiqueta = i18n.t(`dashboardGeneral.periods.${periodo}`);
        return desde === hasta ? `${etiqueta} · ${desde}` : `${etiqueta} · ${desde} — ${hasta}`;
    }, [periodo, date]);

    const rangoAnuncios = useMemo(() => {
        const r = rangoDePeriodo(periodo, date);
        return {
            from: r.from.clone().startOf("day").toISOString(),
            to: r.to.clone().endOf("day").toISOString()
        };
    }, [periodo, date]);

    async function fetchData(overrides = {}) {

        setLoading(true);

        const grupo = overrides.grupo !== undefined ? overrides.grupo : grupoSeleccionado;
        const agente = overrides.agente !== undefined ? overrides.agente : agenteSeleccionado;

        const per = overrides.periodo !== undefined ? overrides.periodo : periodo;
        const dia = overrides.dia !== undefined ? overrides.dia : date;
        const rango = rangoDePeriodo(per, dia);
        const comparar = rangoComparacion(per, dia);

        let params = {
            dateFrom: rango.from.startOf("day").toISOString(),
            dateTo: rango.to.endOf("day").toISOString(),
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            compareFrom: comparar.from.startOf("day").toISOString(),
            compareTo: comparar.to.endOf("day").toISOString()
        };
        if (grupo !== "" && grupo !== null && grupo !== undefined) {
            params.queueGroupId = grupo;
        }
        if (agente !== "" && agente !== null && agente !== undefined) {
            params.filterUserId = agente;
        }
        ultimosParams.current = params;

        const data = await find(params);

        setSlaTargets(data.slaTargets ?? null);
        setFirstReplyP50(data.firstReplyP50 ?? null);
        setAdsDaily(Array.isArray(data.adsDaily) ? data.adsDaily : []);
        setScopesDaily(Array.isArray(data.scopesDaily) ? data.scopesDaily : []);
        setDailySeries(Array.isArray(data.dailySeries) ? data.dailySeries : []);
        setCierresPorDia(Array.isArray(data.closuresByUserDay) ? data.closuresByUserDay : []);
        setNewVsReturning(data.newVsReturning ?? { news: 0, returning: 0 });
        setPrevious(data.previous ?? null);
        setReopensByReason(Array.isArray(data.reopensByReason) ? data.reopensByReason : []);
        setReopensDaily(Array.isArray(data.reopensDaily) ? data.reopensDaily : []);
        setRatingsByUser(Array.isArray(data.ratingsByUser) ? data.ratingsByUser : []);
        if (data.ratingScale) setRatingScale(data.ratingScale);
        setOpportunityFlow(Array.isArray(data.opportunityFlow) ? data.opportunityFlow : []);
        setOpportunityStock(Array.isArray(data.opportunityStock) ? data.opportunityStock : []);
        setOpportunityFlowDaily(Array.isArray(data.opportunityFlowDaily) ? data.opportunityFlowDaily : []);
        if (data.ticketsByQueue) {
            setTicketsByQueue(data.ticketsByQueue);
        }
        if (data.ticketsByUser) {
            setTicketsByUser(data.ticketsByUser);
            if (!agente) {
                setAgentesDelGrupo(data.ticketsByUser);
            }
        }
        if (data.ticketsTotal) {
            setTicketsTotal(data.ticketsTotal);
        }
        if (data.ticketsByAd) {
            setTicketsByAd(data.ticketsByAd);
        }
        if (data.callsSummary) {
            setCallsSummary(data.callsSummary);
        }
        setCallsByLine(Array.isArray(data.callsByLine) ? data.callsByLine : []);
        if (typeof data.workingAgents === "number") {
            setWorkingAgents(data.workingAgents);
        }
        if (data.dailyChats) {
            setDailyChats(data.dailyChats);
        }
        if (data.flowSummary) {
            setFlowSummary(data.flowSummary);
        }
        if (data.ticketsByStatus) {
            setTicketsByStatus(data.ticketsByStatus);
        }
        if (data.ticketByConection) {
            setTicketByConection(data.ticketByConection);
        }
        setLoading(false);

        return data;
    }

    const toggleAmbito = (clave) => {
        setAmbitosDesplegados((prev) =>
            prev.includes(clave) ? prev.filter((x) => x !== clave) : [...prev, clave]
        );
    };

    const handleRowClickAsesor = (row) => {
        setAsesorDetalle(row);
        setAgentClosures([]);

        const principal = (row.categories || [])
            .slice()
            .sort((a, b) => (b.total || 0) - (a.total || 0))[0];

        if (principal) {
            loadAgentClosures(row, principal.name || '');
        } else {
            setAmbitoAbierto(null);
        }
    };

    const loadAgentClosures = async (asesor, nombre) => {
        setAmbitoAbierto(nombre);
        setCargandoLista(true);
        try {
            const rango = rangoDePeriodo(periodo, date);
            const { data } = await api.get("/dashboard/agent-closures", {
                params: {
                    userId: asesor.userId,
                    dateFrom: rango.from.startOf("day").toISOString(),
                    dateTo: rango.to.endOf("day").toISOString(),
                    category: nombre ?? "",
                    queueGroupId: grupoSeleccionado || undefined
                }
            });
            setAgentClosures(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("No se pudo cargar el detalle de cierres:", err?.message);
            setAgentClosures([]);
        } finally {
            setCargandoLista(false);
        }
    };

    const abrirDetalleLlamadas = async (status) => {
        setModalLlamadas(true);
        setCargandoLlamadas(true);
        try {
            const rango = rangoDePeriodo(periodo, date);
            const { data } = await api.get("/dashboard/call-detail", {
                params: {
                    dateFrom: rango.from.startOf("day").toISOString(),
                    dateTo: rango.to.endOf("day").toISOString(),
                    status: status || undefined
                }
            });
            setListaLlamadas(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("No se pudo cargar el detalle de llamadas:", err?.message);
            setListaLlamadas([]);
        } finally {
            setCargandoLlamadas(false);
        }
    };

    const openAdDetail = async ({ adId, category, label }) => {
        setAdDetail({ adId, category, label });
        setAdLoading(true);
        try {
            const { data } = await api.get("/dashboard/ad-detail", {
                params: { adId, category: category ?? undefined }
            });
            setAdRows(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("No se pudo cargar el detalle del anuncio:", err?.message);
            setAdRows([]);
        } finally {
            setAdLoading(false);
        }
    };

    const abrirDetalleNotas = async ({ userId, label }) => {
        setModalNotas({ userId, label });
        setCargandoNotas(true);
        try {
            const rango = rangoDePeriodo(periodo, date);
            const { data } = await api.get("/dashboard/rating-detail", {
                params: {
                    userId: userId ?? undefined,
                    dateFrom: rango.from.startOf("day").toISOString(),
                    dateTo: rango.to.endOf("day").toISOString(),
                    queueGroupId: grupoSeleccionado || undefined
                }
            });
            setListaNotas(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("No se pudo cargar el detalle de calificaciones:", err?.message);
            setListaNotas([]);
        } finally {
            setCargandoNotas(false);
        }
    };

    const openClosures = async ({ category, userId, label }) => {
        setClosureList({ category, userId, label });
        setClosureLoading(true);
        try {
            const rango = rangoDePeriodo(periodo, date);
            const { data } = await api.get("/dashboard/agent-closures", {
                params: {
                    userId: userId ?? undefined,
                    dateFrom: rango.from.startOf("day").toISOString(),
                    dateTo: rango.to.endOf("day").toISOString(),
                    category: category ?? "",
                    queueGroupId: grupoSeleccionado || undefined
                }
            });
            setClosureRows(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("No se pudo cargar el detalle de cierres:", err?.message);
            setClosureRows([]);
        } finally {
            setClosureLoading(false);
        }
    };

    const handleAbrirAmbito = (nombre) => {
        if (ambitoAbierto === nombre) return;
        loadAgentClosures(asesorDetalle, nombre);
    };

    const handleRowClickByConection = (group) => {
        setTicketDetailByConection(group);
        setOpenModalByConection(true);
    };

    const handleRowClickByStatus = (group) => {
        setTicketDetailByStatus(group);
        setSelectedUser(null);
        setOpenModalByStatus(true);
    };

    const handleUserSelect = (user) => {
        setSelectedUser((actual) => (actual === user ? null : user));
    };

    const openStatusDetailByKey = (statusKey) => {
        const group = groupedTicketsByStatus.find((g) => g.name === statusKey);
        if (group) handleRowClickByStatus(group);
    };

    const handleCloseModalDetailsByStatus = () => {
        setOpenModalByStatus(false);
        setTicketDetailByStatus(null);
        setSelectedUser(null);
    };

    const handleCloseModalDetailsByConection = () => {
        setOpenModalByConection(false);
        setTicketDetailByConection(null);
    };

    const IconChannel = (channel) => {
        switch (channel) {
            case "facebook":
                return <Facebook style={{ color: "#3b5998" }} />;
            case "instagram":
                return <Instagram style={{ color: "#e1306c" }} />;
            case "whatsapp":
                return <WhatsApp style={{ color: "#25d366" }} />;
            case "whatsapp_cloud":
                return <WhatsApp style={{ color: "#25d3c1" }} />;
            case "webchat":
                return <Language style={{ fontSize: 20, color: "#3956ff" }} />;
            case "sandbox":
                return <Science style={{ fontSize: 20, color: "#8b8f9a" }} />;
            default:
                return null;
        }
    };

    const Status = (status) => {
        switch (status) {
            case "pending":
                return i18n.t('dashboardGeneral.statusLabels.pending');
            case "open":
                return i18n.t('dashboardGeneral.statusLabels.open');
            case "closed":
                return i18n.t('dashboardGeneral.statusLabels.closed');
            case "bot":
                return i18n.t('dashboardGeneral.statusLabels.bot');
            case "favorite":
                return i18n.t('dashboardGeneral.statusLabels.favorite');
            default:
                return "";
        }
    };

    const pending = Number(ticketsTotal.pending) || 0;
    const open = Number(ticketsTotal.open) || 0;
    const closed = Number(ticketsTotal.closed) || 0;
    const bot = Number(ticketsTotal.bot) || 0;
    const totalActivo = pending + open + bot;
    const totalGeneral = totalActivo + closed;
    const resolutionRate = totalGeneral > 0 ? (closed / totalGeneral) * 100 : 0;
    const botRate = totalGeneral > 0 ? (bot / totalGeneral) * 100 : 0;

    const reopens = Number(flowSummary.reopens) || 0;
    const reopenBase = Number(flowSummary.closed) || 0;
    const reopenRate = reopenBase > 0 ? (reopens / reopenBase) * 100 : null;
    const reopenTarget = Number(slaTargets?.reopen) > 0
        ? Number(slaTargets.reopen)
        : REOPEN_TARGET_DEFAULT;
    const reopenLevel = reopenRate == null
        ? null
        : reopenRate >= reopenTarget * REOPEN_BAD_FACTOR
            ? "bad"
            : reopenRate >= reopenTarget
                ? "warn"
                : "ok";

    const prevBase = Number(previous?.reopenBase) || 0;
    const prevReopenRate = prevBase > 0
        ? ((Number(previous?.reopens) || 0) / prevBase) * 100
        : null;
    const reopenDelta = reopenRate != null && prevReopenRate != null
        ? reopenRate - prevReopenRate
        : null;
    const reopenTrend = reopenDelta == null || Math.abs(reopenDelta) < 1
        ? null
        : reopenDelta > 0
            ? "up"
            : "down";

    const totalAgents = ticketsByUser.length;
    const trabajando = workingAgents || usersWithActivo.length;
    const avgLoad = trabajando > 0 ? (totalActivo / trabajando) : 0;

    const scopesSerie = useMemo(() => {
        const TOPE = 4;
        if (!scopesDaily.length) return { dias: [], series: [] };

        const peso = new Map();
        for (const f of scopesDaily) {
            peso.set(f.scope, (peso.get(f.scope) || 0) + f.total);
        }
        const orden = [...peso.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
        const top = orden.slice(0, TOPE);
        const hayOtros = orden.length > TOPE;

        const porDia = new Map();
        for (const f of scopesDaily) {
            if (!porDia.has(f.day)) porDia.set(f.day, { day: f.day, valores: {} });
            const v = porDia.get(f.day).valores;
            const clave = top.includes(f.scope) ? f.scope : "__otros__";
            v[clave] = (v[clave] || 0) + f.total;
        }

        const dias = [];
        for (let i = 13; i >= 0; i--) {
            const d = moment().subtract(i, "day").format("YYYY-MM-DD");
            dias.push(porDia.get(d) || { day: d, valores: {} });
        }

        const series = top.map((k, i) => ({
            key: k,
            name: k || i18n.t('dashboardGeneral.sections.noReason'),
            color: COLORES_AMBITO[i % COLORES_AMBITO.length]
        }));
        if (hayOtros) {
            series.push({
                key: "__otros__",
                name: i18n.t('dashboardGeneral.sections.otherScopes'),
                color: "#8a8a83"
            });
        }
        return { dias, series };
    }, [scopesDaily]);

    const queuesByGroup = useMemo(() => {
        const porGrupo = new Map();
        for (const c of ticketsByQueue) {
            const clave = c.queueGroupId == null ? "__sin__" : String(c.queueGroupId);
            if (!porGrupo.has(clave)) {
                porGrupo.set(clave, {
                    clave,
                    nombre: c.queueGroupName || i18n.t("dashboardGeneral.sections.ungrouped"),
                    colas: [],
                    total: 0
                });
            }
            const g = porGrupo.get(clave);
            g.colas.push(c);
            g.total += c.totalChats || 0;
        }
        const lista = [...porGrupo.values()];
        for (const g of lista) g.colas.sort((a, b) => (b.totalChats || 0) - (a.totalChats || 0));
        lista.sort((a, b) => {
            if (a.clave === "__sin__") return 1;
            if (b.clave === "__sin__") return -1;
            return a.nombre.localeCompare(b.nombre);
        });
        return lista;
    }, [ticketsByQueue]);

    const ambitoConAsesores = useMemo(() => {
        const mapa = new Map();
        ticketsByUser.forEach((u) => {
            (u.categories || []).forEach((c) => {
                const clave = c.name || '';
                if (!mapa.has(clave)) mapa.set(clave, { nombre: clave, total: 0, asesores: [] });
                const amb = mapa.get(clave);
                amb.total += c.total || 0;
                amb.asesores.push({ userId: u.userId, userName: u.userName, total: c.total || 0 });
            });
        });

        const lista = [...mapa.values()];
        lista.forEach((a) => a.asesores.sort((x, y) => y.total - x.total));
        return lista.sort((a, b) => b.total - a.total);
    }, [ticketsByUser]);


    const [descargando, setDescargando] = useState(false);

    const [kpiDetalle, setKpiDetalle] = useState(null);
    const [kpiFilas, setKpiFilas] = useState([]);

    const [configPdfAbierto, setConfigPdfAbierto] = useState(false);
    const [bloquesPdf, setBloquesPdf] = useState(leerBloquesGuardados);

    const [previaUrl, setPreviaUrl] = useState("");
    const [previaCargando, setPreviaCargando] = useState(false);
    const [previaError, setPreviaError] = useState("");
    const [previaHojas, setPreviaHojas] = useState(1);
    const [previaHoja, setPreviaHoja] = useState(1);
    const [busquedaBloque, setBusquedaBloque] = useState("");

    const previaUrlRef = useRef("");
    const [kpiCargando, setKpiCargando] = useState(false);

    const abrirKpi = async (kpi, titulo, extra = {}) => {
        setKpiDetalle({ kpi, titulo });
        setKpiFilas([]);
        setKpiCargando(true);
        try {
            const p = ultimosParams.current || {};
            const { data } = await api.get("/dashboard/kpi-detail", {
                params: {
                    kpi,
                    dateFrom: p.dateFrom,
                    dateTo: p.dateTo,
                    queueGroupId: p.queueGroupId,
                    filterUserId: p.filterUserId,
                    ...extra
                }
            });
            setKpiFilas(Array.isArray(data) ? data : []);
        } catch (err) {
            toastError(err);
        }
        setKpiCargando(false);
    };

    const resumenFiltros = useMemo(() => {

        const partes = [];

        const agente = agenteSeleccionado
            ? (agentesDelGrupo.find((a) => String(a.userId) === String(agenteSeleccionado))?.userName)
            : i18n.t('dashboardGeneral.allAgents');
        if (agente) partes.push(agente);

        if (grupoSeleccionado === "") {
            partes.push(i18n.t('dashboardGeneral.allGroups'));
        } else if (grupoSeleccionado === "none") {
            partes.push(i18n.t('dashboardGeneral.sections.ungrouped'));
        } else if (grupoSeleccionado) {
            const g = queueGroups.find((x) => String(x.id) === String(grupoSeleccionado));
            if (g) partes.push(g.name);
        }

        partes.push(i18n.t('dashboardGeneral.rightNow'));

        return partes.join("  ·  ");
    }, [agenteSeleccionado, agentesDelGrupo, grupoSeleccionado, queueGroups]);

    const sinTildes = (t) =>
        String(t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const bloquesFiltrados = useMemo(() => {
        const q = sinTildes(busquedaBloque).trim();
        if (!q) return BLOQUES_PDF;

        return BLOQUES_PDF.filter(
            (b) => sinTildes(b.etiqueta).includes(q) || sinTildes(b.desc).includes(q)
        );
    }, [busquedaBloque]);

    const paramsReporte = () => {
        let adsCurrency = "USD";
        try {
            adsCurrency = localStorage.getItem("adCampaignsCurrency") === "COP" ? "COP" : "USD";
        } catch {
        }
        return {
            ...(ultimosParams.current || {}),
            scopeLabel: resumenFiltros,
            bloques: bloquesPdf.join(","),
            adsCurrency,
        };
    };

    const alternarBloque = (clave) => {
        setBloquesPdf((previos) => {
            const siguiente = previos.includes(clave)
                ? previos.filter((c) => c !== clave)
                : [...previos, clave];

            try {
                window.localStorage.setItem(CLAVE_BLOQUES_PDF, JSON.stringify(siguiente));
            } catch {
            }

            return siguiente;
        });
    };

    useEffect(() => {
        if (!configPdfAbierto || !bloquesPdf.length) return undefined;

        const control = new AbortController();
        const temporizador = setTimeout(async () => {
            setPreviaCargando(true);
            setPreviaError("");

            try {
                const respuesta = await api.get("/dashboard/report.pdf", {
                    params: paramsReporte(),
                    responseType: "blob",
                    signal: control.signal,
                });

                const hojas = Number(respuesta.headers?.["x-paginas"]) || 1;
                setPreviaHojas(hojas);
                setPreviaHoja(anterior => Math.min(anterior, hojas));

                const url = window.URL.createObjectURL(
                    new Blob([respuesta.data], { type: "application/pdf" })
                );

                if (previaUrlRef.current) window.URL.revokeObjectURL(previaUrlRef.current);
                previaUrlRef.current = url;
                setPreviaUrl(url);
            } catch (err) {
                if (!control.signal.aborted) setPreviaError("No se pudo generar la vista previa.");
            }

            if (!control.signal.aborted) setPreviaCargando(false);
        }, RETARDO_PREVIA);

        return () => {
            clearTimeout(temporizador);
            control.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configPdfAbierto, bloquesPdf, resumenFiltros]);

    useEffect(() => {
        if (configPdfAbierto) return;

        if (previaUrlRef.current) {
            window.URL.revokeObjectURL(previaUrlRef.current);
            previaUrlRef.current = "";
        }

        setPreviaUrl("");
        setPreviaError("");
        setPreviaHojas(1);
        setPreviaHoja(1);
    }, [configPdfAbierto]);

    const descargarPdf = async () => {

        if (descargando) return;

        const bajar = (url) => {
            const a = document.createElement("a");
            a.href = url;
            a.download = `reporte-tablero-${moment().format("YYYY-MM-DD")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        };

        if (previaUrlRef.current && !previaCargando) {
            bajar(previaUrlRef.current);
            return;
        }

        setDescargando(true);
        try {
            const { data } = await api.get("/dashboard/report.pdf", {
                params: paramsReporte(),
                responseType: "blob"
            });

            const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            bajar(url);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toastError(err);
        }
        setDescargando(false);
    };

    const periodScope = useMemo(() => {
        const base = resumenFiltros.split("  ·  ").slice(0, -1);
        base.push(periodo === "dia" && date
            ? moment(date).format("DD/MM/YYYY")
            : i18n.t(`dashboardGeneral.periods.${periodo}`));
        return base.join("  ·  ");
    }, [resumenFiltros, periodo, date]);

    const hour = new Date().getHours();
    const greeting = hour < 12
        ? i18n.t('dashboardGeneral.greeting.morning')
        : hour < 19
            ? i18n.t('dashboardGeneral.greeting.afternoon')
            : i18n.t('dashboardGeneral.greeting.evening');

    const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const MONTH_KEYS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
    const now = new Date();
    const weekdayName = i18n.t(`dashboardGeneral.weekdays.${WEEKDAY_KEYS[now.getDay()]}`);
    const monthName = i18n.t(`dashboardGeneral.months.${MONTH_KEYS[now.getMonth()]}`);
    const isEnglish = i18n.language?.startsWith('en');
    const todayLabelCap = isEnglish
        ? `${weekdayName}, ${monthName} ${now.getDate()}`
        : `${weekdayName} ${now.getDate()} ${i18n.t('dashboardGeneral.dateJoiner')} ${monthName}`;

    return (
        <Box style={{ flexGrow: 1, padding: 24 }} className="area-imprimible">
            <div className="solo-imprimir">
                <div className="encabezado-impresion" style={{ borderBottomColor: colorImpresion }}>
                    <div className="marca">
                        {logoImpresion && (
                            <span className="logo" style={{ backgroundColor: colorImpresion }}>
                                <img src={logoImpresion} alt="" />
                            </span>
                        )}
                        <div>
                            <div className="titulo">Reporte del tablero</div>
                            <div className="subtitulo">{periodoImpresion}  ·  {resumenFiltros.split("  ·  ").slice(0, -1).join("  ·  ")}</div>
                        </div>
                    </div>
                    <div className="generado">
                        Impreso por {user?.name || ""}
                        <br />
                        {moment().format("DD/MM/YYYY HH:mm")}
                    </div>
                </div>
            </div>
            <Grid container spacing={3}>
                <Grid size={12} className="no-imprimir">
                    <Card>
                        <CardContent className={classes.headerRow}>
                            <Box className={classes.greetingRow}>
                                <Box className={classes.greetingAvatar}>
                                    {(user?.name || "?").charAt(0).toUpperCase()}
                                </Box>
                                <Box>
                                    <Typography className={classes.greetingText}>
                                        {greeting} <strong>{user.name}</strong>
                                    </Typography>
                                    <Typography className={classes.greetingDate}>
                                        {todayLabelCap}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={classes.filterColumn}>
                            <Box className={classes.filterRow}>
                            <Box className={classes.dateFilterControl}>

                                {queueGroups.length > 0 && (
                                    <select
                                        className={classes.groupFilterSelect}
                                        style={{ colorScheme: theme.palette.mode }}
                                        value={grupoSeleccionado}
                                        onChange={(e) => handleCambioGrupo(e.target.value)}
                                    >
                                        <option value="">
                                            {i18n.t('dashboardGeneral.allGroups')}
                                        </option>
                                        {queueGroups.map((grupo) => (
                                            <option key={grupo.id} value={grupo.id}>
                                                {grupo.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {agentesDelGrupo.length > 0 && (
                                    <select
                                        className={classes.groupFilterSelect}
                                        style={{ colorScheme: theme.palette.mode }}
                                        value={agenteSeleccionado}
                                        onChange={(e) => handleCambioAgente(e.target.value)}
                                    >
                                        <option value="">
                                            {i18n.t('dashboardGeneral.allAgents')}
                                        </option>
                                        {agentesDelGrupo.map((a) => (
                                            <option key={a.userId} value={a.userId}>
                                                {a.userName}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <PeriodSelect
                                    className={classes.groupFilterSelect}
                                    value={periodo}
                                    onChange={(valor) => handleCambioPeriodo(valor)}
                                    opciones={PERIODOS.map((k) => ({
                                        valor: k,
                                        etiqueta: i18n.t(`dashboardGeneral.periods.${k}`)
                                    }))}
                                />
                                {periodo === "dia" && (
                                    <input
                                        type="date"
                                        className={classes.dateFilterInput}
                                        value={date}
                                        onChange={(e) => {
                                            setDate(e.target.value);
                                            fetchData({ dia: e.target.value });
                                        }}
                                    />
                                )}

                                <ButtonWithSpinner
                                    loading={loading}
                                    onClick={() => fetchData()}
                                    variant="contained"
                                    color="primary"
                                    className={classes.dateFilterButton}
                                >
                                    {i18n.t('dashboardGeneral.filterButton')}
                                </ButtonWithSpinner>
                            </Box>

                                {}
                                {(user?.profile === "admin" || user?.profile === "supervisor") && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        size="small"
                                        className={classes.botonReporte}
                                        startIcon={<FileDownloadOutlined />}
                                        onClick={() => setConfigPdfAbierto(true)}
                                        disabled={descargando}
                                    >
                                        {descargando ? "Generando…" : "Descargar reporte"}
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    disableElevation
                                    size="small"
                                    className={classes.botonReporte}
                                    startIcon={<PrintOutlined />}
                                    onClick={imprimir}
                                    disabled={imprimiendo || loading}
                                >
                                    {imprimiendo ? "Preparando…" : "Imprimir"}
                                </Button>
                            </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {}
                <Grid size={12}>
                    <Grid container spacing={2} alignItems="flex-start">
                    {}
                    <Grid size={{ xs: 6, sm: 4, md: 4, lg: "grow" }}>
                        <StatTile
                            label={i18n.t('dashboardGeneral.kpis.backlogNow')}
                            value={pending}
                            sub={`${open}${i18n.t('dashboardGeneral.kpis.workingSuffix')}`}
                            hint={i18n.t('dashboardGeneral.kpis.hints.backlogNow')}
                            loading={loading}
                            accent={colorOf('pending')}
                            icon={<AssignmentLateIcon />}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 4, lg: "grow" }}>
                        <StatTile
                            label={i18n.t('dashboardGeneral.kpis.incoming')}
                            onClick={() => abrirKpi("incoming", i18n.t('dashboardGeneral.kpis.incoming'))}
                            value={dailyChats.chats}
                            delta={variacion(dailyChats.chats, previous?.chats)}
                            deltaTitle={rotuloComparacion}
                            sub={dailyChats.contacts > 0
                                ? `${dailyChats.contacts}${i18n.t('dashboardGeneral.kpis.contactsSuffix')}`
                                : undefined}
                            hint={i18n.t('dashboardGeneral.kpis.hints.incoming')}
                            loading={loading}
                            accent={colorOf('open')}
                            icon={<PeopleAltOutlinedIcon />}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 4, lg: "grow" }}>
                        <StatTile
                            label={i18n.t('dashboardGeneral.kpis.closedToday')}
                            onClick={() => abrirKpi("closed", i18n.t('dashboardGeneral.kpis.closedToday'))}
                            delta={variacion(closed, previous?.closed)}
                            deltaTitle={rotuloComparacion}
                            value={closed}
                            hint={i18n.t('dashboardGeneral.kpis.hints.closed')}
                            loading={loading}
                            accent={colorOf('closed')}
                            icon={<AssignmentTurnedInIcon />}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 4, lg: "grow" }}>
                        <StatTile
                            label={i18n.t('dashboardGeneral.kpis.newClients')}
                            onClick={() => abrirKpi("news", i18n.t('dashboardGeneral.kpis.newClients'))}
                            value={newVsReturning.news}
                            sub={porcentajeDe(newVsReturning.news)}
                            hint={i18n.t('dashboardGeneral.kpis.hints.newClients')}
                            loading={loading}
                            accent={colorOf('bot')}
                            icon={<SyncAltIcon />}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 4, lg: "grow" }}>
                        <StatTile
                            label={i18n.t('dashboardGeneral.kpis.returningClients')}
                            onClick={() => abrirKpi("returning", i18n.t('dashboardGeneral.kpis.returningClients'))}
                            value={newVsReturning.returning}
                            sub={porcentajeDe(newVsReturning.returning)}
                            hint={i18n.t('dashboardGeneral.kpis.hints.returningClients')}
                            loading={loading}
                            accent={colorOf('closed')}
                            icon={<PeopleAltOutlinedIcon />}
                        />
                    </Grid>
                    </Grid>
                </Grid>

                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 6
                    }}>
                    {}
                    <Paper className={classes.sectionPaper} style={{ marginBottom: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <AssignmentTurnedInIcon />{i18n.t('dashboardGeneral.scopesTitle')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.scopes')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>
                                {resumenFiltros.replace(i18n.t('dashboardGeneral.rightNow'), i18n.t('dashboardGeneral.trendScope'))}
                            </Typography>
                        </Box>
                        <MultiArea
                            data={scopesSerie.dias}
                            series={scopesSerie.series}
                            emptyLabel={i18n.t('dashboardGeneral.scopesEmpty')}
                        />
                    </Paper>

                    <Paper className={classes.sectionPaper} style={{ marginBottom: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <TimelineIcon />{i18n.t('dashboardGeneral.trendBlock.title')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.trend')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{resumenFiltros.replace(i18n.t('dashboardGeneral.rightNow'), i18n.t('dashboardGeneral.trendScope'))}</Typography>
                        </Box>
                        {dailySeries.length > 0 ? (
                            <DailyTrend
                                data={dailySeries}
                                colorIncoming={colorOf('open')}
                                colorClosed={colorOf('closed')}
                                labelIncoming={i18n.t('dashboardGeneral.kpis.incoming')}
                                labelClosed={i18n.t('dashboardGeneral.kpis.closedToday')}
                            />
                        ) : (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.trendBlock.empty')}
                            </Typography>
                        )}
                    </Paper>

                    <Paper className={classes.sectionPaper}>
                        {}
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <DonutSmallIcon />{i18n.t('dashboardGeneral.sections.statusOverview')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.statusOverview')} />
                            </Typography>
                            <Typography className={classes.sectionScope} title={resumenFiltros}>
                                {resumenFiltros}
                            </Typography>
                        </Box>
                        {}
                        <SegmentedBar
                            items={[
                                { name: i18n.t('dashboardGeneral.statusNames.pending'), value: pending, color: colorOf('pending'), statusKey: "pending" },
                                { name: i18n.t('dashboardGeneral.statusNames.open'), value: open, color: colorOf('open'), statusKey: "open" },
                                { name: i18n.t('dashboardGeneral.statusNames.bot'), value: bot, color: colorOf('bot'), statusKey: "bot" },
                            ]}
                            onSegmentClick={(item) => openStatusDetailByKey(item.statusKey)}
                        />
                    </Paper>
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        {}
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <PeopleAltOutlinedIcon />{i18n.t('dashboardGeneral.sections.byAgent')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.byAgent')} />
                            </Typography>

                            <Button
                                size="small"
                                variant="contained"
                                className="no-imprimir"
                                startIcon={<CompareArrowsIcon />}
                                onClick={() => setCompararAbierto(true)}
                                style={{ textTransform: "none", whiteSpace: "nowrap" }}
                            >
                                Comparar asesores
                            </Button>
                        </Box>
                        <TableContainer style={{ maxHeight: 420, overflowY: 'auto', overflowX: 'hidden' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'userName'}
                                                direction={orderBy === 'userName' ? order : 'asc'}
                                                onClick={() => handleRequestSort('userName')}
                                            >
                                                {i18n.t('dashboardGeneral.table.agent')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={orderBy === 'open'}
                                                direction={orderBy === 'open' ? order : 'asc'}
                                                onClick={() => handleRequestSort('open')}
                                            >
                                                {i18n.t('dashboardGeneral.table.working')}
                                            </TableSortLabel>
                                            <InfoHint text={i18n.t('dashboardGeneral.help.colWorking')} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={orderBy === 'attended'}
                                                direction={orderBy === 'attended' ? order : 'asc'}
                                                onClick={() => handleRequestSort('attended')}
                                            >
                                                Atendidos
                                            </TableSortLabel>
                                            <InfoHint text={i18n.t('dashboardGeneral.help.colAttended')} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={orderBy === 'closed'}
                                                direction={orderBy === 'closed' ? order : 'asc'}
                                                onClick={() => handleRequestSort('closed')}
                                            >
                                                {i18n.t('dashboardGeneral.table.closed')}
                                            </TableSortLabel>
                                            <InfoHint text={i18n.t('dashboardGeneral.help.colClosed')} />
                                        </TableCell>
                                        {}
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={orderBy === 'timeP50'}
                                                direction={orderBy === 'timeP50' ? order : 'asc'}
                                                onClick={() => handleRequestSort('timeP50')}
                                            >
                                                {i18n.t('dashboardGeneral.table.medianTime')}
                                            </TableSortLabel>
                                            <InfoHint text={i18n.t('dashboardGeneral.help.colMedianTime')} />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedData.length > 0 ? (
                                        sortedData.map((row, index) => (
                                            <TableRow
                                                key={index}
                                                hover
                                                className={cx(
                                                    classes.filaAsesor,
                                                    Number(row.userId) === Number(user?.id) && classes.filaPropia
                                                )}
                                                onClick={() => handleRowClickAsesor(row)}
                                                title={i18n.t('dashboardGeneral.table.clickForDetail')}
                                            >
                                                <TableCell>
                                                    {row.userName}
                                                </TableCell>
                                                <TableCell align="center" style={{ fontWeight: 700 }}>{row.open}</TableCell>
                                                {}
                                                <TableCell align="center" style={{ fontWeight: 700 }}>
                                                    {row.attended || 0}
                                                </TableCell>
                                                <TableCell align="center">{row.closed}</TableCell>
                                                {}
                                                <TableCell
                                                    align="center"
                                                    style={{ fontVariantNumeric: "tabular-nums" }}
                                                    title={row.timeP90 != null
                                                        ? `p90: ${row.timeP90} min  ·  ${row.sessions} ${i18n.t('dashboardGeneral.table.sessions')}`
                                                        : undefined}
                                                >
                                                    {row.timeP50 != null ? `${row.timeP50} min` : "-"}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} className={classes.emptyState}>
                                                {i18n.t('dashboardGeneral.noResults')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    {}
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <AssignmentTurnedInIcon />{i18n.t('dashboardGeneral.sections.whoClosesWhat')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.whoClosesWhat')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{resumenFiltros}</Typography>
                        </Box>
                        {ambitoConAsesores.length > 0 ? (
                            <Box className={classes.scrollBox}>
                            <SegmentedBar
                                items={ambitoConAsesores.map((amb, i) => ({
                                    name: amb.nombre || i18n.t('dashboardGeneral.sections.noReason'),
                                    value: amb.total,
                                    color: COLORES_AMBITO[i % COLORES_AMBITO.length],
                                    clave: amb.nombre || ''
                                }))}
                                onSegmentClick={(item) => toggleAmbito(item.clave)}
                            />

                            {}
                            {ambitoConAsesores
                                .filter((amb) => ambitosDesplegados.includes(amb.nombre || ''))
                                .map((amb) => (
                                    <Box key={amb.nombre || 'sin'} className={classes.bloqueAsesores}>
                                        {}
                                        <Typography
                                            className={cx(classes.tituloBloqueAsesores, classes.clickableTitle)}
                                            onClick={() => openClosures({
                                                category: amb.nombre || '',
                                                userId: null,
                                                label: `${i18n.t('dashboardGeneral.table.closuresOf')} ${amb.nombre || i18n.t('dashboardGeneral.sections.noReason')} · ${i18n.t('dashboardGeneral.allAgents')}`
                                            })}
                                            title={i18n.t('dashboardGeneral.table.clickForDetail')}
                                        >
                                            {amb.nombre || i18n.t('dashboardGeneral.sections.noReason')}
                                        </Typography>
                                        {amb.asesores.map((a) => (
                                            <Box
                                                key={a.userId}
                                                className={cx(classes.filaAsesorAmbito, classes.clickableRow)}
                                                onClick={() => openClosures({
                                                    category: amb.nombre || '',
                                                    userId: a.userId,
                                                    label: `${a.userName} · ${amb.nombre || i18n.t('dashboardGeneral.sections.noReason')}`
                                                })}
                                                title={i18n.t('dashboardGeneral.table.clickForDetail')}
                                            >
                                                <span className={classes.nombreAsesorAmbito} title={a.userName}>
                                                    {a.userName}
                                                </span>
                                                <span className={classes.totalAmbito}>{a.total}</span>
                                                <span className={classes.pctAmbito}>
                                                    {Math.round((a.total / Math.max(1, amb.total)) * 100)}%
                                                </span>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography className={classes.emptyState}>{i18n.t('dashboardGeneral.noResults')}</Typography>
                        )}
                    </Paper>

                    {}
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        <AdsPanel dateFrom={rangoAnuncios.from} dateTo={rangoAnuncios.to} onOpenDetail={openAdDetail} />
                    </Paper>


                </Grid>

                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 6
                    }}>
                    {}
                    <Paper className={classes.sectionPaper} style={{ marginBottom: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <HistoryIcon />{i18n.t('dashboardGeneral.flow.title')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.flow')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{i18n.t('dashboardGeneral.flow.source')}</Typography>
                        </Box>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid size={{ xs: 6, sm: 4 }}>
                                {}
                                <StatTile
                                    label={i18n.t('dashboardGeneral.flow.firstReply')}
                                    value={firstReplyP50 != null ? `${firstReplyP50} min` : '-'}
                                    {...objetivo(firstReplyP50, slaTargets?.firstReply)}
                                    hint={i18n.t('dashboardGeneral.flow.firstReplyHint')}
                                    loading={loading} accent={colorOf('neutral')} icon={<HistoryIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.flow.wait')}
                                    value={flowSummary.waitP50 != null ? `${flowSummary.waitP50} min` : '-'}
                                    sub={flowSummary.waitP90 != null
                                        ? `${i18n.t('dashboardGeneral.flow.nineOfTen')} ${flowSummary.waitP90} min`
                                        : undefined}
                                    {...objetivo(flowSummary.waitP50, slaTargets?.wait)}
                                    delta={variacion(flowSummary.waitP50, previous?.waitP50)}
                                    deltaGoodWhenUp={false}
                                    deltaTitle={rotuloComparacion}
                                    hint={i18n.t('dashboardGeneral.flow.waitHint')}
                                    loading={loading} accent={colorOf('neutral')} icon={<AssignmentLateIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.flow.handle')}
                                    value={flowSummary.handleP50 != null ? `${flowSummary.handleP50} min` : '-'}
                                    sub={flowSummary.handleP90 != null
                                        ? `${i18n.t('dashboardGeneral.flow.nineOfTen')} ${flowSummary.handleP90} min`
                                        : undefined}
                                    {...objetivo(flowSummary.handleP50, slaTargets?.handle)}
                                    hint={i18n.t('dashboardGeneral.flow.handleHint')}
                                    loading={loading} accent={colorOf('neutral')} icon={<HistoryIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.flow.reopens')}
                                    value={reopenRate != null
                                        ? `${reopenRate.toFixed(1)}%`
                                        : flowSummary.reopens}
                                    delta={variacion(flowSummary.reopens, previous?.reopens)}
                                    deltaGoodWhenUp={false}
                                    deltaTitle={rotuloComparacion}
                                    sub={reopenRate != null
                                        ? i18n.t('dashboardGeneral.flow.reopensRate', {
                                            reopens,
                                            closed: reopenBase,
                                        })
                                        : i18n.t('dashboardGeneral.flow.reopensNoBase')}
                                    subColor={reopenLevel === "bad"
                                        ? "#c62828"
                                        : reopenLevel === "warn"
                                            ? "#b45309"
                                            : undefined}
                                    {...({
                                            target: i18n.t('dashboardGeneral.flow.reopensMax', {
                                                target: reopenTarget,
                                            }),
                                            targetTone: reopenLevel || "neutral",
                                            targetLabel: i18n.t('dashboardGeneral.flow.reopensTarget', {
                                                label: slaTargets?.configured
                                                    ? i18n.t('dashboardGeneral.flow.target')
                                                    : i18n.t('dashboardGeneral.flow.defaultTarget'),
                                                target: reopenTarget,
                                                bad: reopenTarget * REOPEN_BAD_FACTOR,
                                            }),
                                    })}
                                    hint={i18n.t('dashboardGeneral.flow.reopensHint')}
                                    loading={loading} accent={colorOf('neutral')} icon={<SyncAltIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.flow.transfers')}
                                    value={flowSummary.transfers}
                                    hint={i18n.t('dashboardGeneral.flow.transfersHint')}
                                    loading={loading} accent={colorOf('neutral')} icon={<SyncAltIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.flow.returns')}
                                    value={flowSummary.returns}
                                    hint={i18n.t('dashboardGeneral.flow.returnsHint')}
                                    loading={loading} accent={colorOf('neutral')} icon={<AssignmentLateIcon />}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {}
                    <Paper className={classes.sectionPaper}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <AccountTreeOutlinedIcon />{i18n.t('dashboardGeneral.sections.byQueue')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.byQueue')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{resumenFiltros}</Typography>
                        </Box>
                        {queuesByGroup.length > 0 ? (
                            <Box className={classes.scrollBox}>
                                <Box className={classes.queueHead}>
                                    <span />
                                    {['open', 'pending', 'bot'].map((k) => (
                                        <span key={k} className={classes.queueColLabel}>
                                            {i18n.t(`dashboardGeneral.statusNames.${k}`)}
                                        </span>
                                    ))}
                                    <span className={classes.queueColTotalLabel}>{i18n.t('dashboardGeneral.table.total')}</span>
                                </Box>
                                {queuesByGroup.map((grupo) => (
                                    <React.Fragment key={grupo.clave}>
                                        {queuesByGroup.length > 1 && (
                                            <Box className={classes.queueGroupRow}>
                                                <span className={classes.queueGroupName}>{grupo.nombre}</span>
                                                <span className={classes.queueGroupTotal}>{grupo.total}</span>
                                            </Box>
                                        )}
                                        {grupo.colas.map((row) => {
                                            const vacia = (row.totalChats || 0) === 0;
                                            return (
                                                <Box key={row.queueId} className={classes.queueRow}>
                                                    <span
                                                        className={cx(classes.queueName, vacia && classes.cellZero)}
                                                        title={row.queueName}
                                                    >
                                                        {row.queueName || i18n.t('dashboardGeneral.sections.noQueue')}
                                                    </span>
                                                    {['open', 'pending', 'bot'].map((k) => (
                                                        <span
                                                            key={k}
                                                            className={cx(classes.cellNum, !row[k] && classes.cellZero)}
                                                            style={row[k] ? { color: colorOf(k) } : undefined}
                                                            title={i18n.t(`dashboardGeneral.statusNames.${k}`)}
                                                        >
                                                            {row[k] || 0}
                                                        </span>
                                                    ))}
                                                    <span className={cx(classes.cellTotal, vacia && classes.cellZero)}>
                                                        {row.totalChats || 0}
                                                    </span>
                                                </Box>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </Box>
                        ) : (
                            <Typography className={classes.emptyState}>{i18n.t('dashboardGeneral.noResults')}</Typography>
                        )}
                    </Paper>

                    {}
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <SyncAltIcon />{i18n.t('dashboardGeneral.sections.byLine')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.byLine')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{periodScope}</Typography>
                        </Box>
                        {groupedTickets.length > 0 ? (
                            <Box className={classes.scrollBox}>
                                <Box className={classes.connHead}>
                                    <span />
                                    {['open', 'pending', 'bot', 'closed'].map((k) => (
                                        <span key={k} className={classes.queueColLabel}>
                                            {i18n.t(`dashboardGeneral.statusNames.${k}`)}
                                        </span>
                                    ))}
                                    <span className={classes.queueColTotalLabel}>{i18n.t('dashboardGeneral.table.total')}</span>
                                </Box>
                                {groupedTickets.map((row, index) => (
                                    <Box
                                        key={index}
                                        className={cx(classes.connRow, classes.connClickable)}
                                        onClick={() => handleRowClickByConection(row)}
                                        title={i18n.t('dashboardGeneral.table.clickForDetail')}
                                    >
                                        <span className={classes.connName}>
                                            <span className={classes.connIcon}>{IconChannel(row.channel)}</span>
                                            <span className={classes.connText} title={row.name}>
                                                {row.name || i18n.t('dashboardGeneral.table.noConnection')}
                                            </span>
                                        </span>
                                        {['open', 'pending', 'bot', 'closed'].map((k) => (
                                            <span
                                                key={k}
                                                className={cx(classes.cellNum, !row[k] && classes.cellZero)}
                                                style={row[k] ? { color: colorOf(k) } : undefined}
                                                title={i18n.t(`dashboardGeneral.statusNames.${k}`)}
                                            >
                                                {row[k] || 0}
                                            </span>
                                        ))}
                                        <span className={classes.cellTotal}>{row.total}</span>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography className={classes.emptyState}>{i18n.t('dashboardGeneral.noResults')}</Typography>
                        )}
                    </Paper>

                    {}
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <SyncAltIcon />{i18n.t('dashboardGeneral.sections.calls')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.calls')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{periodScope}</Typography>
                        </Box>
                        <Grid spacing={2} container>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.callsBlock.received')}
                                    value={callsSummary.total || 0}
                                    onClick={callsSummary.total > 0 ? () => abrirDetalleLlamadas() : undefined}
                                    loading={loading}
                                    accent={colorOf('open')}
                                    icon={<AssignmentIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.callsBlock.rated')}
                                    value={callsSummary.rated || 0}
                                    sub={callsSummary.total
                                        ? `${Math.round((callsSummary.rated / callsSummary.total) * 100)}%`
                                        : undefined}
                                    onClick={callsSummary.rated > 0 ? () => abrirDetalleLlamadas('rated') : undefined}
                                    loading={loading}
                                    accent={colorOf('closed')}
                                    icon={<AssignmentTurnedInIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.callsBlock.pending')}
                                    value={callsSummary.pending || 0}
                                    sub={callsSummary.failed
                                        ? `${callsSummary.failed} ${i18n.t('dashboardGeneral.callsBlock.failed')}`
                                        : undefined}
                                    subColor={callsSummary.failed ? red[600] : undefined}
                                    hint={i18n.t('dashboardGeneral.callsBlock.pendingHint')}
                                    onClick={callsSummary.pending > 0 ? () => abrirDetalleLlamadas('pending') : undefined}
                                    loading={loading}
                                    accent={colorOf('pending')}
                                    icon={<AssignmentLateIcon />}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <StatTile
                                    label={i18n.t('dashboardGeneral.callsBlock.colRating')}
                                    value={callsSummary.avgRating != null
                                        ? <RatingStars
                                            value={callsSummary.avgRating}
                                            scale={callsSummary.scale || 3}
                                            size={23}
                                          />
                                        : '-'}
                                    sub={callsSummary.avgRating != null
                                        ? `${callsSummary.avgRating} ${i18n.t('dashboardGeneral.callsBlock.avgOf')} ${callsSummary.scale || 3}`
                                        : callsSummary.total > 0
                                            ? i18n.t('dashboardGeneral.callsBlock.noRatings')
                                            : i18n.t('dashboardGeneral.callsBlock.noCalls')}
                                    loading={loading}
                                    accent={colorOf('bot')}
                                    icon={<DonutSmallIcon />}
                                />
                            </Grid>
                        </Grid>

                        {}
                        {callsByLine.length > 1 && (
                            <Box style={{ marginTop: 14 }}>
                                <Typography className={classes.oppColTitle} style={{ marginBottom: 6 }}>
                                    {i18n.t('dashboardGeneral.callsBlock.byLine')}
                                </Typography>
                                {callsByLine.map((linea, i) => (
                                    <Box key={linea.whatsappId ?? i} className={classes.lineRow}>
                                        <span className={classes.lineName} title={linea.name || linea.number}>
                                            {linea.name || '-'}
                                            {linea.number && (
                                                <span className={classes.lineNumber}>{linea.number}</span>
                                            )}
                                        </span>
                                        <span className={classes.cellTotal}>{linea.total}</span>
                                        <span className={classes.cellNum}>{linea.rated}</span>
                                        <span className={classes.cellNum}>
                                            {linea.avgRating != null
                                                ? <RatingStars value={linea.avgRating} scale={callsSummary.scale || 3} size={16} />
                                                : '-'}
                                        </span>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Paper>

                    {}
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <StarBorderIcon />{i18n.t('dashboardGeneral.ratingsBlock.title')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.ratings')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{periodScope}</Typography>
                        </Box>
                        {ratingsByUser.length > 0 ? (
                            <><Box className={classes.scrollBox}>
                                <Box className={classes.ratingHead}>
                                    <span />
                                    <span className={classes.queueColLabel}>
                                        {i18n.t('dashboardGeneral.ratingsBlock.average')}
                                    </span>
                                    <span className={classes.queueColLabel}>
                                        {i18n.t('dashboardGeneral.ratingsBlock.spread')}
                                    </span>
                                    <span className={classes.queueColTotalLabel}>
                                        {i18n.t('dashboardGeneral.ratingsBlock.count')}
                                    </span>
                                </Box>
                                {ratingsByUser.map((r) => (
                                    <Box
                                        key={r.userId}
                                        onClick={() => abrirDetalleNotas({ userId: r.userId, label: r.userName })}
                                        title={i18n.t('dashboardGeneral.table.clickForDetail')}
                                        style={{ cursor: 'pointer' }}
                                        className={cx(
                                            classes.ratingRow,
                                            String(r.userId) === String(user?.id) && classes.ownRow
                                        )}
                                    >
                                        <span className={classes.queueName} title={r.userName}>
                                            {r.userName || '-'}
                                        </span>
                                        <span className={classes.ratingStarsCell}>
                                            <RatingStars
                                                value={r.avg}
                                                scale={ratingScale || 3}
                                                size={15}
                                            />
                                            <em className={classes.ratingAvgNum}>{r.avg}</em>
                                        </span>
                                        {}
                                        <span className={classes.ratingSpread}>
                                            {(r.distribution || []).map((n, i) => (
                                                n > 0 ? (
                                                    <i
                                                        key={i}
                                                        style={{
                                                            flexGrow: n,
                                                            backgroundColor: RATING_COLORS[i]
                                                        }}
                                                        title={`${i + 1}★: ${n}`}
                                                    />
                                                ) : null
                                            ))}
                                        </span>
                                        <span className={classes.cellTotal}>{r.total}</span>
                                    </Box>
                                ))}
                            </Box>
                            <Box className={classes.callsFooter}>
                                <Button
                                    size="small"
                                    className="no-imprimir"
                                    onClick={() => abrirDetalleNotas({
                                        userId: null,
                                        label: i18n.t('dashboardGeneral.ratingsBlock.detailAll')
                                    })}
                                >
                                    {i18n.t('dashboardGeneral.ratingsBlock.seeDetail')}
                                </Button>
                            </Box>
                            </>
                        ) : (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.ratingsBlock.empty')}
                            </Typography>
                        )}
                    </Paper>

                    {}
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <GroupIcon />{i18n.t('dashboardGeneral.opportunitiesBlock.opportunities')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.opportunities')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{periodScope}</Typography>
                        </Box>
                        <Box className={classes.oppSplit}>
                            {[
                                {
                                    clave: 'flow',
                                    kpi: 'opportunityFlow',
                                    titulo: i18n.t('dashboardGeneral.opportunitiesBlock.flowTitle'),
                                    datos: opportunityFlow,
                                    vacio: i18n.t('dashboardGeneral.opportunitiesBlock.flowEmpty'),
                                    color: colorOf('open'),
                                },
                                {
                                    clave: 'stock',
                                    kpi: 'opportunityStock',
                                    titulo: i18n.t('dashboardGeneral.opportunitiesBlock.stockTitle'),
                                    datos: opportunityStock,
                                    vacio: i18n.t('dashboardGeneral.opportunitiesBlock.stockEmpty'),
                                    color: colorOf('closed'),
                                },
                            ].map((col) => (
                                <Box key={col.clave} className={classes.oppCol}>
                                    <Typography className={classes.oppColTitle}>{col.titulo}</Typography>
                                    {col.datos.length > 0 ? (
                                        col.datos.map((e) => (
                                            <Box
                                                key={e.name}
                                                className={cx(classes.oppRow, e.total > 0 && classes.clickableRow)}
                                                onClick={() => {
                                                    if (!e.total || !e.id) return;
                                                    abrirKpi(col.kpi, `${col.titulo} · ${e.name}`, { statusId: e.id });
                                                }}
                                                title={e.total ? i18n.t('dashboardGeneral.table.clickForDetail') : undefined}
                                            >
                                                <span className={classes.oppName} title={e.name}>
                                                    {e.name}
                                                </span>
                                                <span
                                                    className={cx(classes.cellNum, !e.total && classes.cellZero)}
                                                    style={e.total ? { color: col.color } : undefined}
                                                >
                                                    {e.total}
                                                </span>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography className={classes.emptyState} style={{ padding: 0 }}>
                                            {col.vacio}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                        {}
                        {opportunityFlowDaily.length > 0 && (
                            <Box style={{ marginTop: 14 }}>
                                <MiniArea
                                    data={opportunityFlowDaily}
                                    color={colorOf('open')}
                                    totalLabel={i18n.t('dashboardGeneral.opportunitiesBlock.dailyTotal')}
                                    emptyLabel={i18n.t('dashboardGeneral.opportunitiesBlock.dailyEmpty')}
                                />
                            </Box>
                        )}
                    </Paper>

                    {}
                    <Paper className={classes.sectionPaper} style={{ marginTop: 20 }}>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                                <ReplayIcon />{i18n.t('dashboardGeneral.reopensBlock.title')}
                                <InfoHint text={i18n.t('dashboardGeneral.help.reopens')} />
                            </Typography>
                            <Typography className={classes.sectionScope}>{periodScope}</Typography>
                        </Box>

                        {reopenRate != null && (
                            <Box className={cx(classes.reopenGauge, classes[`reopenGauge_${reopenLevel}`])}>
                                <Box>
                                    <span className={classes.reopenGaugeNum}>
                                        {reopenRate.toFixed(1)}%
                                    </span>
                                    <span className={classes.reopenGaugeText}>
                                        {i18n.t('dashboardGeneral.reopensBlock.rateOf', {
                                            reopens,
                                            closed: reopenBase,
                                        })}
                                    </span>
                                    {reopenTrend && (
                                        <div className={cx(
                                            classes.reopenGaugeTrend,
                                            reopenTrend === "up" ? classes.reopenTrendUp : classes.reopenTrendDown
                                        )}>
                                            {reopenTrend === "up" ? "▲" : "▼"}{" "}
                                            {i18n.t('dashboardGeneral.reopensBlock.vsPrev', {
                                                points: Math.abs(reopenDelta).toFixed(1),
                                                prev: prevReopenRate.toFixed(1),
                                            })}
                                        </div>
                                    )}
                                </Box>
                                <span className={classes.reopenGaugeVerdict}>
                                    {i18n.t(`dashboardGeneral.reopensBlock.verdict.${reopenLevel}`)}
                                </span>
                            </Box>
                        )}

                        {reopensByReason.length > 0 ? (
                            <Box className={classes.scrollBox}>
                                <Box className={classes.reopenHead}>
                                    <span className={classes.queueColLabelLeft}>
                                        {i18n.t('dashboardGeneral.reopensBlock.reason')}
                                    </span>
                                    <span className={classes.queueColLabelLeft}>
                                        {i18n.t('dashboardGeneral.reopensBlock.who')}
                                    </span>
                                    <span className={classes.queueColLabel}>
                                        {i18n.t('dashboardGeneral.reopensBlock.back')}
                                    </span>
                                    <span className={classes.queueColTotalLabel}>
                                        {i18n.t('dashboardGeneral.reopensBlock.count')}
                                    </span>
                                </Box>
                                {reopensByReason.map((r, i) => (
                                    <Box
                                        key={i}
                                        className={cx(classes.reopenRow, r.total > 0 && classes.clickableRow)}
                                        onClick={() => {
                                            if (!r.total) return;
                                            const etiquetaMotivo = r.reason || i18n.t('dashboardGeneral.sections.noReason');
                                            abrirKpi(
                                                'reopens',
                                                `${i18n.t('dashboardGeneral.reopensBlock.title')} · ${etiquetaMotivo}`,
                                                { reason: r.reason ?? '', userId: r.userId ?? '' }
                                            );
                                        }}
                                        title={r.total ? i18n.t('dashboardGeneral.table.clickForDetail') : undefined}
                                    >
                                        <span
                                            className={classes.reopenBar}
                                            style={{ width: `${(r.total / maxReopens) * 100}%` }}
                                        />
                                        <span className={classes.queueName} title={r.reason}>
                                            {r.reason || i18n.t('dashboardGeneral.sections.noReason')}
                                        </span>
                                        <span className={classes.queueName} title={r.userName}>
                                            {r.userName || '-'}
                                        </span>
                                        {}
                                        <span className={cx(
                                            classes.cellNum,
                                            r.avgHours != null && r.avgHours < 4 && classes.reopenFast
                                        )}>
                                            {r.avgHours != null
                                                ? r.avgHours + i18n.t('dashboardGeneral.reopensBlock.hours')
                                                : '-'}
                                        </span>
                                        <span className={classes.cellTotal}>{r.total}</span>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.reopensBlock.empty')}
                            </Typography>
                        )}
                        {}
                        {reopensDaily.length > 0 && (
                            <Box style={{ marginTop: 14 }}>
                                <MiniArea
                                    data={reopensDaily}
                                    color={colorOf('pending')}
                                    totalLabel={i18n.t('dashboardGeneral.reopensBlock.dailyTotal')}
                                    emptyLabel={i18n.t('dashboardGeneral.reopensBlock.dailyEmpty')}
                                />
                            </Box>
                        )}
                    </Paper>

                </Grid>

            </Grid>

            {}
            {}
            <Modal
                open={Boolean(kpiDetalle)}
                onClose={() => setKpiDetalle(null)}
                aria-labelledby="kpi-detalle-titulo"
            >
                <Paper className={classes.modalAsesor}>
                    <Box className={classes.modalAsesorCabecera}>
                        <Box>
                            <Typography className={classes.modalAsesorNombre} id="kpi-detalle-titulo">
                                {kpiDetalle?.titulo}
                            </Typography>
                            <Typography className={classes.modalAsesorSub}>{resumenFiltros}</Typography>
                        </Box>
                        <Box className={classes.modalAsesorCifras}>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{kpiFilas.length}</span>
                                <span className={classes.cifraLbl}>
                                    {kpiFilas.length === 1 ? 'registro' : 'registros'}
                                </span>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.modalAsesorCuerpo}>
                        {kpiCargando ? (
                            <Typography className={classes.emptyState}>...</Typography>
                        ) : kpiFilas.length === 0 ? (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.noResults')}
                            </Typography>
                        ) : (
                            <TableContainer style={{ maxHeight: 420, overflowY: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Contacto</TableCell>
                                            <TableCell>Conexión</TableCell>
                                            <TableCell>Agente</TableCell>
                                            <TableCell>Estado</TableCell>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell>Chat</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {kpiFilas.map((f) => (
                                            <TableRow key={f.ticketId}>
                                                {}
                                                <TableCell title={f.phone}>
                                                    {f.contact || f.phone || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <span className={classes.kpiConn}>
                                                        <span className={classes.connIcon}>
                                                            {IconChannel(f.connectionChannel)}
                                                        </span>
                                                        <span title={f.connection}>
                                                            {f.connection || '—'}
                                                        </span>
                                                    </span>
                                                </TableCell>
                                                <TableCell>{f.agent || '—'}</TableCell>
                                                <TableCell>{f.status}</TableCell>
                                                <TableCell>
                                                    {f.at ? moment(f.at).format('DD/MM HH:mm') : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {f.uuid && (
                                                        <a
                                                            href={`/tickets/${f.uuid}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ textDecoration: 'none', fontWeight: 'bold' }}
                                                        >
                                                            #{f.ticketId}
                                                        </a>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Modal>

            {}
            <Modal
                open={configPdfAbierto}
                onClose={() => setConfigPdfAbierto(false)}
                aria-labelledby="config-pdf-title"
            >
                <Paper className={classes.modalConfigPdf}>
                    <Box className={classes.configCabecera}>
                        <Typography className={classes.modalAsesorNombre} id="config-pdf-title">
                            Descargar reporte
                        </Typography>
                        <Typography className={classes.modalAsesorSub}>
                            Elige qué métricas lleva. Se recuerdan en este navegador.
                        </Typography>
                    </Box>

                    <Divider />

                    <Box className={classes.configCuerpo}>
                        <Box className={classes.configLista}>
                            <TextField
                                className={classes.configBuscar}
                                size="small"
                                fullWidth
                                placeholder="Buscar métrica…"
                                value={busquedaBloque}
                                onChange={(e) => setBusquedaBloque(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchOutlined fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box className={classes.configListaScroll}>
                                {["Cifras", "Equipo", "Operación", "Resultado"].map((grupo) => {
                                    const delGrupo = bloquesFiltrados.filter((b) => b.grupo === grupo);
                                    if (!delGrupo.length) return null;

                                    return (
                                        <Box key={grupo} className={classes.configGrupoBloque}>
                                            <Typography className={classes.configGrupo}>
                                                {grupo}
                                            </Typography>

                                            {delGrupo.map((b) => (
                                                <FormControlLabel
                                                    key={b.clave}
                                                    className={classes.configOpcion}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={bloquesPdf.includes(b.clave)}
                                                            onChange={() => alternarBloque(b.clave)}
                                                            style={{ paddingTop: 2, paddingBottom: 2 }}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography className={classes.opcionTitulo}>
                                                                {b.etiqueta}
                                                            </Typography>
                                                            <Typography className={classes.opcionDesc}>
                                                                {b.desc}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            ))}
                                        </Box>
                                    );
                                })}

                                {!bloquesFiltrados.length && (
                                    <Typography className={classes.configVacio}>
                                        Ninguna métrica coincide con “{busquedaBloque}”.
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box className={classes.configPrevia}>
                            <Typography className={classes.configGrupo}>Vista previa</Typography>
                            <Box className={classes.previaMarco}>
                                {}
                                {previaUrl && (
                                    <iframe
                                        key={`${previaUrl}#${previaHoja}`}
                                        src={`${previaUrl}#page=${previaHoja}&toolbar=0&navpanes=0&view=Fit`}
                                        title="Vista previa del reporte"
                                        className={classes.previaVisor}
                                    />
                                )}

                                {(previaCargando || (!previaUrl && !previaError)) && (
                                    <Box className={classes.previaCapa}>
                                        <CircularProgress size={26} />
                                        <Typography className={classes.previaAviso}>
                                            Generando vista previa…
                                        </Typography>
                                    </Box>
                                )}

                                {previaError && !previaCargando && (
                                    <Box className={classes.previaCapa}>
                                        <Typography className={classes.previaAviso}>
                                            {previaError}
                                        </Typography>
                                    </Box>
                                )}

                                {!bloquesPdf.length && (
                                    <Box className={classes.previaCapa}>
                                        <Typography className={classes.previaAviso}>
                                            Elige al menos una métrica
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {}
                            <Box className={classes.previaPieFila}>
                                {previaHojas > 1 && (
                                    <Button
                                        size="small"
                                        color="inherit"
                                        disabled={previaHoja <= 1}
                                        onClick={() => setPreviaHoja(h => Math.max(1, h - 1))}
                                    >
                                        ‹
                                    </Button>
                                )}

                                <Typography className={classes.previaPie}>
                                    A4 apaisado ·{" "}
                                    {previaHojas > 1
                                        ? `hoja ${previaHoja} de ${previaHojas}`
                                        : "así se descarga"}
                                </Typography>

                                {previaHojas > 1 && (
                                    <Button
                                        size="small"
                                        color="inherit"
                                        disabled={previaHoja >= previaHojas}
                                        onClick={() => setPreviaHoja(h => Math.min(previaHojas, h + 1))}
                                    >
                                        ›
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Divider />

                    <Box className={classes.configPie}>
                        <Button
                            size="small"
                            color="inherit"
                            onClick={() => {
                                setBloquesPdf(BLOQUES_PDF_DEFECTO);
                                try {
                                    window.localStorage.setItem(
                                        CLAVE_BLOQUES_PDF,
                                        JSON.stringify(BLOQUES_PDF_DEFECTO)
                                    );
                                } catch {
                                }
                            }}
                        >
                            Restablecer
                        </Button>

                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Button size="small" color="inherit" onClick={() => setConfigPdfAbierto(false)}>
                                Cancelar
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<FileDownloadOutlined />}
                                disabled={bloquesPdf.length === 0 || descargando}
                                onClick={async () => {
                                    setConfigPdfAbierto(false);
                                    await descargarPdf();
                                }}
                            >
                                {descargando ? "Generando…" : "Descargar"}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Modal>

            <Modal
                open={Boolean(closureList)}

                onClose={() => setClosureList(null)}
                aria-labelledby="closures-title"
            >
                <Paper className={classes.modalAsesor}>
                    <Box className={classes.modalAsesorCabecera}>
                        <Box>
                            <Typography className={classes.modalAsesorNombre} id="closures-title">
                                {closureList?.label}
                            </Typography>
                            <Typography className={classes.modalAsesorSub}>{resumenFiltros}</Typography>
                        </Box>
                        <Box className={classes.modalAsesorCifras}>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{closureRows.length}</span>
                                <span className={classes.cifraLbl}>
                                    {i18n.t('dashboardGeneral.statusNames.closed')}
                                </span>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.modalAsesorCuerpo}>
                        {closureLoading ? (
                            <Typography className={classes.emptyState}>...</Typography>
                        ) : closureRows.length === 0 ? (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.noResults')}
                            </Typography>
                        ) : (
                            <TableContainer style={{ maxHeight: 420, overflowY: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colDate')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colContact')}</TableCell>
                                            {}
                                            {!closureList?.userId && (
                                                <TableCell>{i18n.t('dashboardGeneral.table.user')}</TableCell>
                                            )}
                                            <TableCell>{i18n.t('dashboardGeneral.table.colReason')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colComment')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colLine')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colChat')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {closureRows.map((f) => (
                                            <TableRow key={f.eventId}>
                                                <TableCell>{moment(f.occurredAt).format('DD/MM HH:mm')}</TableCell>
                                                <TableCell title={f.phone}>{f.contact || f.phone || '-'}</TableCell>
                                                {!closureList?.userId && (
                                                    <TableCell>{f.agent || '-'}</TableCell>
                                                )}
                                                <TableCell>{f.reason || '-'}</TableCell>
                                                {}
                                                <TableCell title={f.comment} className={classes.commentCell}>
                                                    {f.comment || '-'}
                                                </TableCell>
                                                <TableCell title={f.connection}>{f.connection || '-'}</TableCell>
                                                <TableCell>
                                                    {}
                                                    {f.uuid ? (
                                                        <a
                                                            href={`/tickets/${f.uuid}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={i18n.t('dashboardGeneral.table.openChat')}
                                                        >
                                                            #{f.ticketId}
                                                        </a>
                                                    ) : (
                                                        <span className={classes.cellZero}>
                                                            {i18n.t('dashboardGeneral.table.noChat')}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    <div className={classes.modalAsesorPie}>
                        <Typography className={classes.modalAsesorSub}>
                            {closureRows.length >= 300
                                ? i18n.t('dashboardGeneral.callsBlock.limitNote')
                                : ''}
                        </Typography>
                        <Button onClick={() => setClosureList(null)}>
                            {i18n.t('dashboardGeneral.close')}
                        </Button>
                    </div>
                </Paper>
            </Modal>

            {}
            <Modal
                open={Boolean(adDetail)}
                onClose={() => setAdDetail(null)}
                aria-labelledby="ad-detail-title"
            >
                <Paper className={classes.modalAsesor}>
                    <Box className={classes.modalAsesorCabecera}>
                        <Box>
                            <Typography className={classes.modalAsesorNombre} id="ad-detail-title">
                                {adDetail?.label}
                            </Typography>
                            <Typography className={classes.modalAsesorSub}>
                                {i18n.t('dashboardGeneral.adsScope')}
                            </Typography>
                        </Box>
                        <Box className={classes.modalAsesorCifras}>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{adRows.length}</span>
                                <span className={classes.cifraLbl}>
                                    {i18n.t('dashboardGeneral.table.total')}
                                </span>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.modalAsesorCuerpo}>
                        {adLoading ? (
                            <Typography className={classes.emptyState}>...</Typography>
                        ) : adRows.length === 0 ? (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.noResults')}
                            </Typography>
                        ) : (
                            <TableContainer style={{ maxHeight: 420, overflowY: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colDate')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colContact')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.user')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colReason')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colLine')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.table.colChat')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {adRows.map((f) => (
                                            <TableRow key={f.ticketId}>
                                                <TableCell>{moment(f.arrivedAt).format('DD/MM HH:mm')}</TableCell>
                                                <TableCell title={f.phone}>{f.contact || f.phone || '-'}</TableCell>
                                                <TableCell>{f.agent || '-'}</TableCell>
                                                <TableCell>{f.reason || '-'}</TableCell>
                                                <TableCell title={f.connection}>{f.connection || '-'}</TableCell>
                                                <TableCell>
                                                    {}
                                                    {f.uuid ? (
                                                        <a
                                                            href={`/tickets/${f.uuid}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={i18n.t('dashboardGeneral.table.openChat')}
                                                        >
                                                            #{f.ticketId}
                                                        </a>
                                                    ) : (
                                                        <span className={classes.cellZero}>
                                                            {i18n.t('dashboardGeneral.table.noChat')}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    <div className={classes.modalAsesorPie}>
                        <Typography className={classes.modalAsesorSub}>
                            {adRows.length >= 300
                                ? i18n.t('dashboardGeneral.callsBlock.limitNote')
                                : ''}
                        </Typography>
                        <Button onClick={() => setAdDetail(null)}>
                            {i18n.t('dashboardGeneral.close')}
                        </Button>
                    </div>
                </Paper>
            </Modal>

            {}
            <Modal
                open={modalLlamadas}
                onClose={() => setModalLlamadas(false)}
                aria-labelledby="modal-llamadas-titulo"
            >
                <Paper className={classes.modalAsesor}>
                    <Box className={classes.modalAsesorCabecera}>
                        <Box>
                            <Typography className={classes.modalAsesorNombre} id="modal-llamadas-titulo">
                                {i18n.t('dashboardGeneral.callsBlock.detailTitle')}
                            </Typography>
                            <Typography className={classes.modalAsesorSub}>{periodScope}</Typography>
                        </Box>
                        <Box className={classes.modalAsesorCifras}>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{callsSummary.total || 0}</span>
                                <span className={classes.cifraLbl}>
                                    {i18n.t('dashboardGeneral.callsBlock.received')}
                                </span>
                            </Box>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{callsSummary.rated || 0}</span>
                                <span className={classes.cifraLbl}>
                                    {i18n.t('dashboardGeneral.callsBlock.rated')}
                                </span>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.modalAsesorCuerpo}>
                        {callsSummary.rated > 0 && (
                            <Box className={classes.callsBreakdown}>
                                {[...(callsSummary.distribution || [])]
                                    .map((n, i) => ({ nota: i + 1, total: n }))
                                    .reverse()
                                    .map(({ nota, total }) => (
                                        <Box key={nota} className={classes.callsRow}>
                                            <RatingStars
                                                value={nota}
                                                scale={callsSummary.scale || 3}
                                                size={14}
                                            />
                                            <span className={cx(classes.cellNum, !total && classes.cellZero)}>
                                                {total}
                                            </span>
                                            <span className={classes.pctAmbito}>
                                                {Math.round((total / Math.max(1, callsSummary.rated)) * 100)}%
                                            </span>
                                        </Box>
                                    ))}
                            </Box>
                        )}
                        {cargandoLlamadas ? (
                            <Typography className={classes.emptyState}>...</Typography>
                        ) : listaLlamadas.length === 0 ? (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.noResults')}
                            </Typography>
                        ) : (
                            <TableContainer style={{ maxHeight: 420, overflowY: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{i18n.t('dashboardGeneral.callsBlock.colDate')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.callsBlock.colContact')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.callsBlock.colPhone')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.callsBlock.colConnection')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.callsBlock.colRating')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.callsBlock.colTicket')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {listaLlamadas.map((ll) => (
                                            <TableRow key={ll.callId}>
                                                <TableCell>
                                                    {moment(ll.occurredAt).format('DD/MM HH:mm')}
                                                </TableCell>
                                                <TableCell>{ll.contact || '-'}</TableCell>
                                                <TableCell>{ll.phone}</TableCell>
                                                <TableCell>{ll.connection || '-'}</TableCell>
                                                <TableCell>
                                                    {ll.rating != null ? (
                                                        <RatingStars
                                                            value={ll.rating}
                                                            scale={callsSummary.scale || 3}
                                                            size={14}
                                                        />
                                                    ) : (
                                                        <span className={classes.cellZero}>
                                                            {i18n.t(`dashboardGeneral.callsBlock.${ll.status === 'failed' ? 'failed' : 'pending'}`)}
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {}
                                                    {ll.uuid ? (
                                                        <a
                                                            href={`/tickets/${ll.uuid}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            #{ll.ticketId}
                                                        </a>
                                                    ) : (
                                                        <span className={classes.cellZero}>
                                                            {i18n.t('dashboardGeneral.callsBlock.noTicket')}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    <div className={classes.modalAsesorPie}>
                        <Typography className={classes.modalAsesorSub}>
                            {listaLlamadas.length >= 300
                                ? i18n.t('dashboardGeneral.callsBlock.limitNote')
                                : ''}
                        </Typography>
                        <Button onClick={() => setModalLlamadas(false)}>
                            {i18n.t('dashboardGeneral.close')}
                        </Button>
                    </div>
                </Paper>
            </Modal>

            {}
            <Modal
                open={Boolean(modalNotas)}
                onClose={() => setModalNotas(null)}
                aria-labelledby="modal-notas-titulo"
            >
                <Paper className={classes.modalAsesor}>
                    <Box className={classes.modalAsesorCabecera}>
                        <Box>
                            <Typography className={classes.modalAsesorNombre} id="modal-notas-titulo">
                                {i18n.t('dashboardGeneral.ratingsBlock.detailTitle')}
                                {modalNotas?.label ? ` · ${modalNotas.label}` : ''}
                            </Typography>
                            <Typography className={classes.modalAsesorSub}>{periodScope}</Typography>
                        </Box>
                        <Box className={classes.modalAsesorCifras}>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{listaNotas.length}</span>
                                <span className={classes.cifraLbl}>
                                    {i18n.t('dashboardGeneral.ratingsBlock.count')}
                                </span>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.modalAsesorCuerpo}>
                        {cargandoNotas ? (
                            <Typography className={classes.emptyState}>...</Typography>
                        ) : listaNotas.length === 0 ? (
                            <Typography className={classes.emptyState}>
                                {i18n.t('dashboardGeneral.noResults')}
                            </Typography>
                        ) : (
                            <TableContainer style={{ maxHeight: 420, overflowY: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{i18n.t('dashboardGeneral.ratingsBlock.colDate')}</TableCell>
                                            {}
                                            {!modalNotas?.userId && (
                                                <TableCell>{i18n.t('dashboardGeneral.ratingsBlock.colAgent')}</TableCell>
                                            )}
                                            <TableCell>{i18n.t('dashboardGeneral.ratingsBlock.colContact')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.ratingsBlock.colRate')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.ratingsBlock.colSource')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.ratingsBlock.colNote')}</TableCell>
                                            <TableCell>{i18n.t('dashboardGeneral.ratingsBlock.colTicket')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {listaNotas.map((n) => (
                                            <TableRow key={n.ratingId}>
                                                <TableCell>
                                                    {moment(n.createdAt).format('DD/MM HH:mm')}
                                                </TableCell>
                                                {!modalNotas?.userId && (
                                                    <TableCell>{n.agent || '-'}</TableCell>
                                                )}
                                                <TableCell title={n.phone}>
                                                    {n.contact || n.phone || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <RatingStars
                                                        value={n.rate}
                                                        scale={ratingScale || 3}
                                                        size={14}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {n.source === 'audit'
                                                        ? i18n.t('dashboardGeneral.ratingsBlock.srcAudit')
                                                            + (n.auditor
                                                                ? ` (${i18n.t('dashboardGeneral.ratingsBlock.byAuditor')} ${n.auditor})`
                                                                : '')
                                                        : i18n.t('dashboardGeneral.ratingsBlock.srcSurvey')}
                                                </TableCell>
                                                <TableCell title={soloObservacion(n.note)}>
                                                    {soloObservacion(n.note) || (
                                                        <span className={classes.cellZero}>
                                                            {i18n.t('dashboardGeneral.ratingsBlock.noNote')}
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {}
                                                    {n.uuid ? (
                                                        <a
                                                            href={`/tickets/${n.uuid}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            #{n.ticketId}
                                                        </a>
                                                    ) : (
                                                        <span className={classes.cellZero}>
                                                            {i18n.t('dashboardGeneral.ratingsBlock.noTicket')}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    <div className={classes.modalAsesorPie}>
                        <Typography className={classes.modalAsesorSub}>
                            {listaNotas.length >= 300
                                ? i18n.t('dashboardGeneral.ratingsBlock.limitNote')
                                : ''}
                        </Typography>
                        <Button onClick={() => setModalNotas(null)}>
                            {i18n.t('dashboardGeneral.close')}
                        </Button>
                    </div>
                </Paper>
            </Modal>

            {}
            <Modal
                open={Boolean(asesorDetalle)}
                onClose={() => setAsesorDetalle(null)}
                aria-labelledby="modal-asesor"
            >
                <Paper className={classes.modalAsesor}>
                    <Box className={classes.modalAsesorCabecera}>
                        <Box>
                            <Typography className={classes.modalAsesorNombre}>
                                {asesorDetalle?.userName}
                            </Typography>
                            <Typography className={classes.modalAsesorSub}>
                                {resumenFiltros}
                            </Typography>
                        </Box>
                        <Box className={classes.modalAsesorCifras}>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{asesorDetalle?.open ?? 0}</span>
                                <span className={classes.cifraLbl}>{i18n.t('dashboardGeneral.table.working')}</span>
                            </Box>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>{asesorDetalle?.closed ?? 0}</span>
                                <span className={classes.cifraLbl}>{i18n.t('dashboardGeneral.table.closed')}</span>
                            </Box>
                            <Box className={classes.cifra}>
                                <span className={classes.cifraNum}>
                                    {asesorDetalle?.timeP50 != null ? asesorDetalle.timeP50 : '-'}
                                </span>
                                <span className={classes.cifraLbl}>{i18n.t('dashboardGeneral.table.medianTime')}</span>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.modalAsesorCuerpo}>
                        <Box className={classes.columnaAmbitos}>
                            <Box className={classes.tituloBloqueModalConTotal}>
                                <Typography className={classes.tituloBloqueModal}>
                                    {i18n.t('dashboardGeneral.table.categories')}
                                </Typography>
                                {}
                                <Typography className={classes.tituloBloqueModalTotal}>
                                    {i18n.t('dashboardGeneral.table.categoriesTotal', {
                                        total: (asesorDetalle?.categories || []).reduce(
                                            (suma, c) => suma + (c.total || 0),
                                            0
                                        )
                                    })}
                                </Typography>
                            </Box>
                            {(asesorDetalle?.categories || []).length === 0 && (
                                <Typography className={classes.listaVacia}>
                                    {i18n.t('dashboardGeneral.noResults')}
                                </Typography>
                            )}
                            {(asesorDetalle?.categories || []).map((c) => {
                                const pct = asesorDetalle.closed > 0
                                    ? Math.round((c.total / asesorDetalle.closed) * 100)
                                    : 0;
                                const activo = ambitoAbierto === (c.name || '');
                                return (
                                    <Box
                                        key={c.name || 'sin'}
                                        className={cx(classes.ambitoItem, activo && classes.ambitoActivo)}
                                        onClick={() => handleAbrirAmbito(c.name || '')}
                                    >
                                        <Box className={classes.ambitoItemTop}>
                                            <span className={classes.ambitoItemNombre}>
                                                {c.name || i18n.t('dashboardGeneral.sections.noReason')}
                                            </span>
                                            <span className={classes.ambitoItemNum}>{c.total}</span>
                                        </Box>
                                        <Box className={classes.barRowRail} style={{ height: 6 }}>
                                            <Box
                                                className={classes.barRowFill}
                                                style={{ width: `${Math.min(100, pct)}%`, backgroundColor: colorOf('open') }}
                                            />
                                        </Box>
                                        <span className={classes.ambitoItemPct}>{pct}%</span>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box className={classes.columnaLista}>
                            <Typography className={classes.tituloBloqueModal}>
                                {ambitoAbierto === null
                                    ? i18n.t('dashboardGeneral.table.pickCategory')
                                    : (ambitoAbierto || i18n.t('dashboardGeneral.sections.noReason'))}
                            </Typography>

                            {ambitoAbierto === null ? (
                                <Typography className={classes.listaVacia}>
                                    {i18n.t('dashboardGeneral.table.pickCategoryHint')}
                                </Typography>
                            ) : cargandoLista ? (
                                <Typography className={classes.listaVacia}>
                                    {i18n.t('dashboardGeneral.loading')}
                                </Typography>
                            ) : agentClosures.length === 0 ? (
                                <Typography className={classes.listaVacia}>
                                    {i18n.t('dashboardGeneral.noResults')}
                                </Typography>
                            ) : (
                                <>
                                    {}
                                    {motivosCierreAsesor.length > 0 && (
                                        <Box className={classes.motivosCierreBox}>
                                            <Typography className={classes.motivosCierreTitulo}>
                                                {i18n.t('dashboardGeneral.table.closeReasonsBreakdown')}
                                            </Typography>
                                            <SegmentedBar items={motivosCierreAsesor} />
                                        </Box>
                                    )}
                                    <Box className={cx(classes.closureRow, classes.closureHeadRow)}>
                                        <span className={classes.closureContact}>{i18n.t('dashboardGeneral.table.colContact')}</span>
                                        <span className={classes.closureReason}>{i18n.t('dashboardGeneral.table.colReason')}</span>
                                        <span className={classes.closureLine}>{i18n.t('dashboardGeneral.table.colLine')}</span>
                                        <span className={classes.closureDate}>{i18n.t('dashboardGeneral.table.colDate')}</span>
                                        <span className={classes.closureChat}>{i18n.t('dashboardGeneral.table.colChat')}</span>
                                    </Box>
                                    <Box className={classes.closureListBox}>
                                        {agentClosures.map((f) => (
                                            <Box key={f.eventId} className={classes.closureRow}>
                                                <span className={classes.closureContact} title={f.phone}>
                                                    {f.contact || f.phone || '-'}
                                                </span>
                                                <span className={classes.closureReason}>{f.reason || '-'}</span>
                                                <span className={classes.closureLine} title={f.connection}>
                                                    {f.connection || '-'}
                                                </span>
                                                <span className={classes.closureDate}>
                                                    {moment(f.occurredAt).format('DD/MM HH:mm')}
                                                </span>
                                                <span className={classes.closureChat}>
                                                    {}
                                                    {f.uuid ? (
                                                        <a
                                                            href={`/tickets/${f.uuid}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={i18n.t('dashboardGeneral.table.openChat')}
                                                        >
                                                            #{f.ticketId}
                                                        </a>
                                                    ) : (
                                                        <span className={classes.cellZero}>
                                                            {i18n.t('dashboardGeneral.table.noChat')}
                                                        </span>
                                                    )}
                                                </span>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>

                    <div className={classes.modalAsesorPie}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => {
                                handleCambioAgente(String(asesorDetalle.userId));
                                setAsesorDetalle(null);
                            }}
                        >
                            {i18n.t('dashboardGeneral.table.filterByAgent')}
                        </Button>
                        <Button size="small" onClick={() => setAsesorDetalle(null)}>
                            {i18n.t('closeReasonModal.buttons.close')}
                        </Button>
                    </div>
                </Paper>
            </Modal>

            <CompararAsesoresModal
                open={compararAbierto}
                onClose={() => setCompararAbierto(false)}
                asesores={todosLosAsesores}
                periodo={i18n.t(`dashboardGeneral.periods.${periodo}`)}
                porDia={cierresPorDia}
                ratingsByUser={ratingsByUser}
            />

            <Modal
                open={openModalByConection}
                onClose={handleCloseModalDetailsByConection}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Paper className={classes.modalDetalle}>
                    <h3 id="modal-title">{IconChannel(ticketDetailByConection?.channel)} {ticketDetailByConection?.name}</h3>
                    <TableContainer style={{ maxHeight: 400, overflowY: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t('dashboardGeneral.table.name')}</TableCell>
                                    <TableCell>{i18n.t('dashboardGeneral.table.state')}</TableCell>
                                    <TableCell>{i18n.t('dashboardGeneral.table.ticket')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(ticketDetailByConection?.details || []).map((ticket, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{ticket.contactname}</TableCell>
                                        <TableCell>{Status(ticket.status)}</TableCell>
                                        <TableCell>
                                            <a target="_blank" style={{ textDecoration: 'none', fontWeight: 'bold' }} href={`tickets/${ticket?.uuid ?? ''}`}>{ticket?.id ?? ''}</a>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Button
                        onClick={handleCloseModalDetailsByConection}
                        variant="contained"
                        color="primary"
                        fullWidth
                    >
                        {i18n.t('dashboardGeneral.close')}
                    </Button>
                </Paper>
            </Modal>
            <Modal
                open={openModalByStatus}
                onClose={handleCloseModalDetailsByStatus}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Paper className={classes.modalDetalle}>
                    <h3 id="modal-title">{i18n.t('dashboardGeneral.recordsOf')} {ticketDetailByStatus?.name}</h3>

                    {}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        {Object.entries(userStatusTotals).map(([user, total]) => (
                            <Button
                                key={user}
                                variant="outlined"
                                onClick={() => handleUserSelect(user)}
                                style={{
                                    backgroundColor: selectedUser === user ? '#1976d2' : 'transparent',
                                    color: selectedUser === user ? 'white' : 'inherit'
                                }}
                            >
                                {user}: {total} {total > 1 ? i18n.t('dashboardGeneral.ticketPlural') : i18n.t('dashboardGeneral.ticketSingular')}
                            </Button>
                        ))}
                    </div>

                    {}
                    <TableContainer style={{ maxHeight: 400, overflowY: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t('dashboardGeneral.table.name')}</TableCell>
                                    <TableCell>{i18n.t('dashboardGeneral.table.lastUser')}</TableCell>
                                    <TableCell>{i18n.t('dashboardGeneral.table.state')}</TableCell>
                                    <TableCell>{i18n.t('dashboardGeneral.table.ticket')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredStatusTickets.map((ticket, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{ticket.name}</TableCell>
                                        <TableCell>{ticket?.user ?? ''}</TableCell>
                                        <TableCell>{Status(ticket.status)}</TableCell>
                                        <TableCell>
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: 'none', fontWeight: 'bold' }}
                                                href={`tickets/${ticket?.uuid ?? ''}`}
                                            >
                                                {ticket?.id ?? ''}
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Button
                        onClick={handleCloseModalDetailsByStatus}
                        variant="contained"
                        color="primary"
                        fullWidth
                        style={{ marginTop: '10px' }}
                    >
                        {i18n.t('dashboardGeneral.close')}
                    </Button>
                </Paper>
            </Modal>
        </Box >
    );
};

export default DashboardGeneral;
